/**
 * Rules — the claim rules Shayna asked for on 2026-10-05: which biller a coinsurance
 * claim is credited to, how much a coinsurance payer's claims weigh and for which
 * billers they count, and which payers are "hard". docs/RULES.md.
 *
 * For app admins: the full-access role AND an 'admin' row (GET /api/dev `admin`),
 * whether or not Dev mode is on. Off while viewing as someone: that is read-only.
 * The API checks the same itself on every request.
 *
 * Redesigned after Yehuda's review on 2026-10-06 ("the UI looks bad"): an admin page
 * without the app's figures and filters (ADMIN_PATHS in AppShell), one tab per rule type,
 * and lists that start from the payers in the data rather than from empty tables, so
 * Shayna sets a rule by ticking a box beside the payer it is about. The API, the tables
 * and the audit are unchanged: a row edited here is an add or a PATCH, as before.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@/auth/AuthProvider'
import { useDevMode } from '@/ui/devMode'
import { viewingAs } from '@/data/viewAs'
import { useClaimDetail } from '@/data/claimDetail'
import { useDataset } from '@/data/DatasetProvider'
import { useWeights } from '@/data/weights'
import { PREVIEW_LABEL, STALE_NOTICE } from '@/data/weightsNote'
import { SearchPick, type PickOption } from '@/components/SearchPick'
import { MultiSelect } from '@/components/MultiSelect'
import { SidePanel } from '@/components/SidePanel'
import { Info } from '@/components/Info'
import { ExportBar } from '@/components/ExportBar'
import { downloadCSV } from '@/ui/export'
import { int, pct } from '@/format'
import { RulesError, rulesApi, type RulesApi } from '@/rules/api'
import { AGE_STEPS, hasAgedCounts, payerTypeTotals, type AgeStep } from '@/data/agedNoTouch'
import { CSV_COLUMNS, rowsForImport } from '@/rules/csv'
import {
  COINS_PAYER_EXPORT,
  HARD_PAYER_EXPORT,
  PAYER_TYPE_EXPORT,
  type CoinsPayerExportRow,
  type HardPayerExportRow,
  type PayerTypeExportRow,
} from '@/rules/shownExport'
import {
  addResults,
  datesNote,
  importSummary,
  isBillDay,
  MAX_DAY,
  MIN_DAY,
  shortDate,
  changesNothing,
  settingBefore,
  PRIMARY_PAYER,
  creditLabel,
  isPrimaryCredit,
  SUGGESTED_BILLER,
  TABLE_LABEL,
  claimsForBillerRule,
  claimsMatching,
  dataStartOf,
  splitAtDataStart,
  hasBillerRule,
  isPattern,
  lineClaims,
  payerCombos,
  payerDays,
  payerTotals,
  previewBiller,
  reorderBiller,
  sortBiller,
  type AuditEntry,
  type BillerPreview,
  type BillerRule,
  type BillerSentence,
  type CoinsurancePayer,
  type PayerTypeSetting,
  type HardPayer,
  type ImportResult,
  type PayerCombo,
  type PayerDays,
  type PayerTotal,
  type RuleSet,
  type RuleTable,
} from '@/rules/model'

type Load<T> = { state: 'loading' } | { state: 'ok'; data: T } | { state: 'error'; message: string }

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e))
const low = (s: string | null | undefined) => (s ?? '').trim().toLowerCase()
const plural = (n: number, one: string, many = `${one}s`) => `${int(n)} ${n === 1 ? one : many}`

function Notice({ what, why }: { what: string; why?: ReactNode }) {
  return (
    <div className="notice" style={{ marginTop: 18 }}>
      <p className="notice-what">{what}</p>
      {why && <p className="notice-why">{why}</p>}
    </div>
  )
}

export function Rules() {
  const dev = useDevMode()
  if (viewingAs()) {
    return (
      <>
        <h1 className="page-title">Claim rules</h1>
        <Notice what="Rules are off while viewing as someone." why="Viewing as is read-only. Exit it to edit rules." />
      </>
    )
  }
  if (dev.features === null) return <span className="eyebrow">Checking your access…</span>
  if (!dev.features.admin) {
    return (
      <>
        <h1 className="page-title">Claim rules</h1>
        <Notice
          what="Rules are limited to app admins."
          why="Admins hold full access and are named in the app’s member list. Ask the app owner."
        />
      </>
    )
  }
  return <Editor />
}

/* ------------------------------------------------------------------ data the lists start from */

/** What the bundle and Claim detail offer to the lists, the pickers and the previews. */
interface Vocab {
  /** Every payer in Claim detail, most claims first. Null until Claim detail arrives. */
  totals: PayerTotal[] | null
  /** Coinsurance claims only, by payer type × payer: what a biller rule is about. */
  coinsCombos: PayerCombo[] | null
  combos: PayerCombo[] | null
  /** Claims by payer, state and bill date: each line's own count (#597). Null until Claim detail arrives. */
  days: PayerDays | null
  /** The first day of the data ('YYYY-MM-DD'): what is in force then heads each payer (#597). Null until the bundle arrives. */
  dataStart: string | null
  payers: PickOption[]
  payerTypes: PickOption[]
  departments: string[]
  states: string[]
  claimsError: string | null
}

function useVocab(): Vocab {
  const { dataset } = useDataset()
  const { data, error } = useClaimDetail(true)
  const combos = useMemo(() => (data ? payerCombos(data) : null), [data])
  const days = useMemo(() => (data ? payerDays(data) : null), [data])
  return useMemo(() => {
    const coinsCombos = combos?.filter((c) => c.coinsurance) ?? null
    const tally = (key: (c: PayerCombo) => string) => {
      const m = new Map<string, number>()
      for (const c of coinsCombos ?? []) m.set(key(c), (m.get(key(c)) ?? 0) + c.claims)
      return m
    }
    const byPayer = tally((c) => c.payer)
    const byType = tally((c) => c.payerType)
    // Coinsurance payers first, by claims; then every other name, A–Z.
    const opts = (counted: Map<string, number>, rest: Iterable<string>): PickOption[] => {
      const seen = new Set(counted.keys())
      return [
        ...[...counted].sort((a, b) => b[1] - a[1]).map(([v, n]) => ({ value: v, label: `${v} · ${int(n)}` })),
        ...[...new Set([...rest].map((x) => x.trim()).filter((x) => x && !seen.has(x)))]
          .sort((a, b) => a.localeCompare(b))
          .map((v) => ({ value: v, label: v })),
      ]
    }
    const uniq = (xs: Iterable<string>) => [...new Set([...xs].map((x) => x.trim()).filter(Boolean))]
    return {
      totals: combos ? payerTotals(combos) : null,
      coinsCombos,
      combos,
      days,
      dataStart: dataStartOf(dataset?.touchMonths.map((t) => t.month)),
      payers: opts(byPayer, (combos ?? []).map((c) => c.payer)),
      // The bundle's payer types too, so the picker works before Claim detail arrives.
      payerTypes: opts(byType, [...(combos ?? []).map((c) => c.payerType), ...(dataset?.crosstab.payers ?? [])]),
      departments: uniq(['Medicare', 'HMO', ...(data?.dict.dept ?? []), ...(dataset?.crosstab.departments ?? [])]).filter(
        (d) => d !== 'Unknown',
      ),
      states: uniq([...(data?.dict.s ?? []), ...(dataset?.crosstab.states ?? [])])
        .filter((s) => /^[A-Za-z]{2}$/.test(s))
        .map((s) => s.toUpperCase())
        .sort(),
      claimsError: error,
    }
  }, [combos, days, data, dataset, error])
}

/* ------------------------------------------------------------------ the page */

type Tab = 'billers' | 'payers' | 'hard' | 'ptypes'

/**
 * `short` is the label on a phone, where all three must fit across.
 *
 * `hidden`: Coinsurance billers is hidden, not removed (Shayna, #229, 2026-10-08: "the
 * coinsurance rule is it follows the assignee of the primary payer"). The model credits
 * each coinsurance claim to whoever its primary claim is assigned to (#228), so no
 * per-biller rule is needed. The tab, its API and the table stay, unread by the app's
 * weighting; ?tab=billers falls back to the first shown tab.
 */
const TABS: { key: Tab; label: string; short: string; table: RuleTable; hidden?: boolean }[] = [
  { key: 'billers', label: 'Coinsurance billers', short: 'Billers', table: 'coinsurance_biller_rule', hidden: true },
  { key: 'payers', label: 'Coinsurance payers', short: 'Payers', table: 'coinsurance_payer' },
  { key: 'hard', label: 'Hard payers', short: 'Hard payers', table: 'hard_payer' },
  // Review #695 (rewritten 2026-10-18): each payer type's 0% touch age limit.
  { key: 'ptypes', label: 'Payer types', short: 'Types', table: 'payer_type_setting' },
]
const SHOWN = TABS.filter((t) => !t.hidden)

type Run = (what: string | (() => string), fn: () => Promise<unknown>) => Promise<boolean>
type HistoryFocus = { table: RuleTable | null; id: number | null; title: string }

interface TabProps {
  rules: RuleSet
  vocab: Vocab
  api: RulesApi
  run: Run
  onHistory: (f: HistoryFocus) => void
}

function Editor() {
  const { token } = useAuth()
  const api = useMemo(() => rulesApi(token), [token])
  const vocab = useVocab()
  const [rules, setRules] = useState<Load<RuleSet>>({ state: 'loading' })
  const [audit, setAudit] = useState<Load<AuditEntry[]>>({ state: 'loading' })
  const [flash, setFlash] = useState<{ ok: boolean; text: string } | null>(null)
  const [history, setHistory] = useState<HistoryFocus | null>(null)
  const [params, setParams] = useSearchParams()
  const tab: Tab = (SHOWN.find((t) => t.key === params.get('tab')) ?? SHOWN[0]!).key
  const setTab = (t: Tab) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p)
        if (t === SHOWN[0]!.key) n.delete('tab')
        else n.set('tab', t)
        return n
      },
      { replace: true },
    )

  const reload = useCallback(() => {
    api
      .list()
      .then((data) => setRules({ state: 'ok', data }))
      .catch((e) => setRules({ state: 'error', message: errText(e) }))
    api
      .audit()
      .then((data) => setAudit({ state: 'ok', data }))
      .catch((e) => setAudit({ state: 'error', message: errText(e) }))
  }, [api])
  useEffect(reload, [reload])

  // A success note fades on its own; a failure stays until dismissed.
  useEffect(() => {
    if (!flash?.ok) return
    const t = window.setTimeout(() => setFlash(null), 3500)
    return () => window.clearTimeout(t)
  }, [flash])

  /** Every write goes through here: say what happened, then re-read. */
  const run = useCallback<Run>(
    async (what, fn) => {
      try {
        await fn()
        setFlash({ ok: true, text: typeof what === 'function' ? what() : what })
        reload()
        return true
      } catch (e) {
        const rows = e instanceof RulesError ? e.rowErrors : []
        setFlash({
          ok: false,
          text:
            errText(e) +
            (rows.length ? ' ' + rows.slice(0, 8).map((r) => `${r.rule ?? `Row ${r.row}`}: ${r.error}`).join(' · ') : ''),
        })
        reload()
        return false
      }
    },
    [reload],
  )

  const counts: Record<Tab, number> | null =
    rules.state === 'ok'
      ? {
          billers: rules.data.biller.filter((r) => r.active).length,
          payers: rules.data.payers.filter((r) => r.active).length,
          hard: rules.data.hard.filter((r) => r.active).length,
          ptypes: (rules.data.payerTypes ?? []).filter((r) => r.active).length,
        }
      : null
  const current = TABS.find((t) => t.key === tab)!
  const tabRows =
    rules.state === 'ok'
      ? { billers: rules.data.biller, payers: rules.data.payers, hard: rules.data.hard, ptypes: rules.data.payerTypes ?? [] }[tab]
      : []

  return (
    <div className="rules-page">
      <div className="rules-head">
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <h1 className="page-title">Claim rules</h1>
          <p className="body-note" style={{ marginTop: 4 }}>
            How much coinsurance and hard payers’ claims count, and each payer type’s 0% touch age limit.
          </p>
          <WhoApplies />
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className="btn" onClick={() => setHistory({ table: null, id: null, title: 'All changes' })}>
            History
          </button>
          <CsvMenu table={current.table} label={current.label} rows={tabRows} api={api} run={run} />
        </div>
      </div>

      <div className="rules-banner" role="note">
        These rules are saved and audited. Payer rules change counts only where weighted claims are switched on; a
        payer type’s age limit changes the 0% touch share everywhere. A coinsurance claim is credited to whoever its
        primary claim is assigned to; the data model does that (Rand).
      </div>

      <div className="rtabs" role="tablist" aria-label="Rule types">
        {SHOWN.map((t) => (
          <button
            key={t.key}
            role="tab"
            id={`rtab-${t.key}`}
            aria-selected={tab === t.key}
            aria-controls="rtab-panel"
            className="rtab"
            onClick={() => setTab(t.key)}
          >
            <span className="rtab-long">{t.label}</span>
            <span className="rtab-short" aria-hidden>
              {t.short}
            </span>
            <span className="rtab-count" aria-label={counts ? `${counts[t.key]} set` : undefined}>
              {counts ? counts[t.key] : '·'}
            </span>
          </button>
        ))}
      </div>

      <div role="tabpanel" id="rtab-panel" aria-labelledby={`rtab-${tab}`} style={{ paddingTop: 16 }}>
        {rules.state === 'loading' && <p className="eyebrow">Loading rules…</p>}
        {rules.state === 'error' && <Notice what={rules.message} />}
        {rules.state === 'ok' && (
          <>
            {tab === 'billers' && <BillersTab rules={rules.data} vocab={vocab} api={api} run={run} onHistory={setHistory} />}
            {tab === 'payers' && <PayersTab rules={rules.data} vocab={vocab} api={api} run={run} onHistory={setHistory} />}
            {tab === 'hard' && <HardTab rules={rules.data} vocab={vocab} api={api} run={run} onHistory={setHistory} />}
            {tab === 'ptypes' && <PayerTypesTab rules={rules.data} vocab={vocab} api={api} run={run} onHistory={setHistory} />}
          </>
        )}
      </div>

      {flash && (
        <div role="status" className={`rules-toast ${flash.ok ? 'ok' : 'bad'}`}>
          <span style={{ flex: 1 }}>{flash.text}</span>
          <button className="rules-x" aria-label="Dismiss" onClick={() => setFlash(null)}>
            ×
          </button>
        </div>
      )}

      {history && (
        <SidePanel onClose={() => setHistory(null)} width={520} label="History">
          <HistoryList load={audit} rules={rules.state === 'ok' ? rules.data : null} focus={history} />
        </SidePanel>
      )}
    </div>
  )
}

/**
 * Who turns these rules into figures (docs/RULES.md, "Who applies the rules"): the model
 * at each refresh once it weighs claims, the app's own preview until then. In the page
 * header, with the stale-rules notice (#113).
 */
function WhoApplies() {
  const w = useWeights()
  if (w.status !== 'ok') return null
  const text =
    w.source === 'preview'
      ? `The model does not apply these rules yet. Until it does, the Weighted claims switch shows the app’s own weighting of them, labelled “${PREVIEW_LABEL}”.`
      : `The model applies these rules at each data refresh${w.rulesVersion ? ` (it last read them ${new Date(w.rulesVersion).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })})` : ''}. A change saved here reaches the figures at the next refresh.`
  return (
    <p className="body-note" style={{ marginTop: 4, maxWidth: 760 }}>
      {text}
      {w.stale && <strong> {STALE_NOTICE}.</strong>}
    </p>
  )
}

/* ------------------------------------------------------------------ small parts */

/** A "⋯" button and its menu. Closes on a pick, a click outside and Escape. */
function Menu({
  items,
  label,
  children,
}: {
  items: { label: string; onClick: () => void; disabled?: boolean }[]
  label: string
  children?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const down = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', down)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', down)
      document.removeEventListener('keydown', key)
    }
  }, [open])
  return (
    <div ref={box} style={{ position: 'relative' }}>
      <button
        className="btn rules-more"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        ⋯
      </button>
      {open && (
        <div role="menu" className="rules-menu">
          {items.map((it) => (
            <button
              key={it.label}
              role="menuitem"
              disabled={it.disabled}
              onClick={() => {
                setOpen(false)
                it.onClick()
              }}
            >
              {it.label}
            </button>
          ))}
          {children}
        </div>
      )}
    </div>
  )
}

/** CSV out and in, for the tab on screen. Kept out of the way: a spreadsheet is the exception. */
function CsvMenu({
  table,
  label,
  rows,
  api,
  run,
}: {
  table: RuleTable
  label: string
  rows: readonly object[]
  api: RulesApi
  run: Run
}) {
  const file = useRef<HTMLInputElement>(null)
  const exportCsv = () =>
    downloadCSV(
      `cpc-${table.replace(/_/g, '-')}.csv`,
      rows,
      CSV_COLUMNS[table].map((f) => ({
        header: f,
        value: (r: object) => {
          const v = (r as Record<string, unknown>)[f]
          return v === null || v === undefined ? null : typeof v === 'boolean' ? String(v) : (v as string | number)
        },
      })),
    )
  const importCsv = async (f: File) => {
    const parsed = rowsForImport(table, await f.text())
    if (!parsed.ok) return void run('', () => Promise.reject(new Error(parsed.error)))
    if (
      !window.confirm(
        `Import ${plural(parsed.rows.length, 'row')} into ${label}? Rows with an id, or a payer already listed, update that rule; the rest are added.`,
      )
    )
      return
    let summary = ''
    await run(
      () => summary,
      async () => {
        const r = await api.importRows(table, parsed.rows)
        summary = `Imported: ${importSummary(r)}`
      },
    )
  }
  return (
    <>
      <Menu
        label="More: CSV"
        items={[
          { label: `Export ${label} (CSV)`, onClick: exportCsv },
          { label: `Import ${label} from CSV…`, onClick: () => file.current?.click() },
        ]}
      />
      <input
        ref={file}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (f) void importCsv(f)
        }}
      />
    </>
  )
}

/** Search box + "only set" filter + how many are showing, above a list. */
function Toolbar({
  q,
  setQ,
  only,
  setOnly,
  onlyLabel,
  shown,
  total,
  noun,
  children,
}: {
  /** Filters beside the search box. */
  children?: ReactNode
  q: string
  setQ: (v: string) => void
  only: boolean
  setOnly: (v: boolean) => void
  onlyLabel: string
  shown: number
  total: number
  noun: string
}) {
  return (
    <div className="rules-toolbar">
      <input
        type="search"
        className="rules-input rules-search"
        placeholder={`Search ${noun}s`}
        aria-label={`Search ${noun}s`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {children}
      <label className="rules-check">
        <input type="checkbox" checked={only} onChange={(e) => setOnly(e.target.checked)} />
        {onlyLabel}
      </label>
      <span className="dim" style={{ marginLeft: 'auto', fontSize: 12 }}>
        {shown === total ? plural(total, noun) : `${int(shown)} of ${plural(total, noun)}`}
      </span>
    </div>
  )
}


/* ------------------------------------------------------------------ payer filters and selection */

/** Payer type and payer filters on the payer lists (Yehuda, 2026-10-06). Empty = all. */
interface PayerFilter {
  types: string[]
  payers: string[]
}

const toggled = (xs: string[], v: string) => (xs.includes(v) ? xs.filter((x) => x !== v) : [...xs, v])

function PayerFilters({
  value,
  onChange,
  totals,
}: {
  value: PayerFilter
  onChange: (f: PayerFilter) => void
  totals: readonly PayerTotal[]
}) {
  const types = useMemo(() => [...new Set(totals.flatMap((t) => t.types))].sort((a, b) => a.localeCompare(b)), [totals])
  const payers = useMemo(() => [...totals.map((t) => t.payer)].sort((a, b) => a.localeCompare(b)), [totals])
  return (
    <>
      <div className="rules-filter">
        <MultiSelect
          label="Payer type"
          options={types}
          value={value.types}
          onToggle={(v) => onChange({ ...value, types: toggled(value.types, v) })}
          onSet={(vs) => onChange({ ...value, types: vs })}
          searchable
        />
      </div>
      <div className="rules-filter">
        <MultiSelect
          label="Payer"
          options={payers}
          value={value.payers}
          onToggle={(v) => onChange({ ...value, payers: toggled(value.payers, v) })}
          onSet={(vs) => onChange({ ...value, payers: vs })}
          searchable
        />
      </div>
    </>
  )
}

/** A payer passes the filters. A pattern rule has no payer types, so a type filter hides it. */
function payerPasses(f: PayerFilter, payer: string, typesOf: ReadonlyMap<string, string[]>): boolean {
  if (f.payers.length && !f.payers.some((p) => low(p) === low(payer))) return false
  if (f.types.length) {
    const ts = typesOf.get(low(payer))
    if (!ts || !ts.some((t) => f.types.includes(t))) return false
  }
  return true
}

const typesByPayer = (totals: readonly PayerTotal[] | null) =>
  new Map((totals ?? []).map((t) => [low(t.payer), t.types] as const))

/** Which lines are ticked, by key. Kept across filters; a bulk edit acts on what is ticked. */
function useSelection(lines: readonly { key: string }[], shown: readonly { key: string }[]) {
  const [sel, setSel] = useState<ReadonlySet<string>>(new Set())
  // A line that is gone (a state setting removed) cannot stay ticked.
  const live = useMemo(() => new Set(lines.map((l) => l.key)), [lines])
  const picked = useMemo(() => new Set([...sel].filter((k) => live.has(k))), [sel, live])
  const shownKeys = shown.map((l) => l.key)
  const allShown = shownKeys.length > 0 && shownKeys.every((k) => picked.has(k))
  const someShown = shownKeys.some((k) => picked.has(k))
  return {
    picked,
    has: (k: string) => picked.has(k),
    toggle: (k: string) => setSel((s) => (s.has(k) ? new Set([...s].filter((x) => x !== k)) : new Set([...s, k]))),
    allShown,
    someShown,
    /** The header box: ticks every line shown, or unticks them when all are. */
    toggleShown: () =>
      setSel((s) => (allShown ? new Set([...s].filter((k) => !shownKeys.includes(k))) : new Set([...s, ...shownKeys]))),
    selectShown: () => setSel((s) => new Set([...s, ...shownKeys])),
    clear: () => setSel(new Set()),
  }
}

/** The header's tick-all box, with the half-ticked state. */
function SelectAllBox({ sel, label }: { sel: ReturnType<typeof useSelection>; label: string }) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = sel.someShown && !sel.allShown
  })
  return (
    <span role="columnheader" className="rules-pick-cell">
      <input ref={ref} type="checkbox" aria-label={label} checked={sel.allShown} onChange={sel.toggleShown} />
    </span>
  )
}

/** Shown while anything is ticked: how many, select all shown, clear, and the bulk edit. */
function SelectBar({
  sel,
  shownCount,
  noun,
  children,
}: {
  sel: ReturnType<typeof useSelection>
  shownCount: number
  noun: string
  children: ReactNode
}) {
  if (sel.picked.size === 0) return null
  return (
    <div className="rules-selectbar" role="region" aria-label={`Selected ${noun}s`}>
      <strong>{plural(sel.picked.size, noun)} selected</strong>
      {!sel.allShown && (
        <button type="button" className="btn" onClick={sel.selectShown}>
          Select all {int(shownCount)} shown
        </button>
      )}
      <button type="button" className="btn" onClick={sel.clear}>
        Clear selection
      </button>
      <span className="rules-bulk">{children}</span>
    </div>
  )
}

/**
 * "Weight [ ] from [date] Reason [ ] Set" for the ticked lines. The date (by bill date)
 * makes it a change from that day rather than for all time (Shayna #162: "dont assume
 * is for all time"); blank applies to every date. The reason (hard payers) goes on every
 * ticked line at once (Shayna #399); each line's own Reason field still overrides it.
 * A reason with no weight sets just the reason, on the lines that have a weight set.
 */
function BulkWeight({
  label,
  min,
  reasonLabel,
  onApply,
}: {
  label: string
  min: number
  /** Offer a reason for all the ticked lines, under this name. */
  reasonLabel?: string
  onApply: (w: number | null, from: string | null, reason: string | null) => void
}) {
  const [text, setText] = useState('')
  const [from, setFrom] = useState('')
  const [reason, setReason] = useState('')
  // A date half filled in ("mm/dd/2026") reads as blank, which would mean every date:
  // the field's own badInput says it is not blank but unfinished.
  const [unfinished, setUnfinished] = useState(false)
  const check = (e: { currentTarget: HTMLInputElement }) => setUnfinished(e.currentTarget.validity.badInput)
  const n = text.trim() === '' ? null : Number(text)
  const weightOk = n !== null && Number.isFinite(n) && n >= min && n <= 100 && Math.abs(n * 10_000 - Math.round(n * 10_000)) < 1e-6
  const dayOk = from === '' ? !unfinished : isBillDay(from)
  const why = reason.trim() || null
  const ok = dayOk && (n !== null ? weightOk : !!why && from === '')
  const hint =
    text.trim() !== '' && !weightOk
      ? `${min} to 100`
      : !dayOk
        ? from === ''
          ? 'Finish the date, or clear it for every date'
          : `Type the whole year (${MIN_DAY.slice(0, 4)}–${MAX_DAY.slice(0, 4)})`
        : n === null && why && from !== ''
          ? 'Type a weight to start from a date'
          : weightOk && from === ''
            ? 'No from date: every bill date'
            : ''
  return (
    <form
      className="rules-bulk-form"
      onSubmit={(e) => {
        e.preventDefault()
        const day = e.currentTarget.querySelector<HTMLInputElement>('input[type=date]')
        if (day?.validity.badInput) return setUnfinished(true)
        if (ok) {
          onApply(n, from || null, why)
          setText('')
          setReason('')
        }
      }}
    >
      <label>
        {label}{' '}
        <input
          className="rules-input rules-num"
          inputMode="decimal"
          value={text}
          aria-label={`${label} for the selected payers`}
          aria-invalid={text.trim() !== '' && !weightOk}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. 1.5"
        />
      </label>
      <label>
        from{' '}
        <input
          type="date"
          className="rules-input rules-date"
          min={MIN_DAY}
          max={MAX_DAY}
          aria-label="From (bill date) for the selected payers; blank = all dates"
          aria-invalid={!dayOk}
          title="Blank: every date. A date: a change from that bill date on."
          value={from}
          onChange={(e) => {
            setFrom(e.target.value)
            check(e)
          }}
          onKeyUp={check}
          onBlur={check}
        />
      </label>
      {reasonLabel && (
        <label>
          {reasonLabel}{' '}
          <input
            className="rules-input rules-bulk-reason"
            maxLength={400}
            value={reason}
            aria-label={`${reasonLabel} for the selected payers`}
            placeholder="Optional, for all"
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
      )}
      <button type="submit" className="btn" disabled={!ok}>
        Set
      </button>
      {hint && <span className="dim">{hint}</span>}
    </form>
  )
}

/**
 * What a blank from date means, and what each line's Claims counts (Shayna #597: "if i
 * dont put in a from date does it auto set for all time?"). Under each payer tab's lede.
 */
function DatesNote() {
  return (
    <p className="dim rules-dates-note">
      No from date: in force for every bill date, until a change from a date takes over from that date on. Each
      payer’s line shows what is in force when the data starts; its later changes and one-state settings sit under
      it (↳), and settings replaced before then are under History. Claims: the claims each line applies to (in its
      state, billed on or after its date).
    </p>
  )
}

/**
 * Sends rows through the import endpoint as a page edit, at most 1,000 to a request.
 * The API skips rows that change nothing and applies a repeated row once (#399).
 */
async function bulkSave(api: RulesApi, table: RuleTable, rows: Record<string, string>[]): Promise<ImportResult> {
  let out: ImportResult = { added: 0, updated: 0, unchanged: 0, repeated: 0 }
  for (let i = 0; i < rows.length; i += 1000) out = addResults(out, await api.importRows(table, rows.slice(i, i + 1000), 'page'))
  return out
}

/**
 * A date to type or pick, saved only once it is whole (#399, "not letting me put in the
 * year"). A date field reports a year typed half-way as a real date (typing 2026 goes
 * 0002, 0020, 0202, 2026), so saving on every change saved Jan 1 of year 1 and closed
 * the field. Enter, Add, or leaving the field with a whole date saves; Escape, or
 * leaving it without one, cancels.
 */
function DayPick({ label, onPick, onCancel }: { label: string; onPick: (day: string) => void; onCancel: () => void }) {
  const [v, setV] = useState('')
  const done = useRef(false)
  const ok = isBillDay(v)
  const finish = (pick: boolean) => {
    if (done.current) return
    done.current = true
    if (pick && ok) onPick(v)
    else onCancel()
  }
  return (
    <form
      className="rules-daypick"
      onSubmit={(e) => {
        e.preventDefault()
        if (ok) finish(true)
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) finish(true)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') finish(false)
      }}
    >
      <input
        type="date"
        className="rules-input rules-date"
        autoFocus
        min={MIN_DAY}
        max={MAX_DAY}
        aria-label={label}
        aria-invalid={v !== '' && !ok}
        title={v !== '' && !ok ? `Type the whole year (${MIN_DAY.slice(0, 4)}–${MAX_DAY.slice(0, 4)})` : undefined}
        value={v}
        onChange={(e) => setV(e.target.value)}
      />
      <button type="submit" className="btn" disabled={!ok}>
        Add
      </button>
    </form>
  )
}


/** How a payer list is ordered: by claims (most first, by default) or by name. */
type Sort = { by: 'claims' | 'payer' | 'type'; desc: boolean }
const BY_CLAIMS: Sort = { by: 'claims', desc: true }

/** Sorts payer rows; ties, and the name sort, go A–Z ignoring case. */
function sortPayers<T>(
  rows: T[],
  sort: Sort,
  payer: (r: T) => string,
  claims: (r: T) => number | null,
  type: (r: T) => string = () => '',
): T[] {
  const byName = (a: T, b: T) => payer(a).localeCompare(payer(b), undefined, { sensitivity: 'base' })
  // By payer type (Shayna #162): a payer with no type in the data sorts last either way.
  const byType = (a: T, b: T) => {
    const x = type(a)
    const y = type(b)
    if (!x !== !y) return !x ? 1 : -1
    return x.localeCompare(y, undefined, { sensitivity: 'base' }) || byName(a, b)
  }
  return [...rows].sort((a, b) => {
    const d =
      sort.by === 'claims' ? (claims(a) ?? -1) - (claims(b) ?? -1) || -byName(a, b) : sort.by === 'type' ? byType(a, b) : byName(a, b)
    return sort.desc && !(sort.by === 'type' && (!type(a) || !type(b))) ? -d : d
  })
}

/** A payer's types as the list shows them: "Managed", or "Managed +1" for several. */
const typesText = (ts: readonly string[] | undefined) =>
  !ts?.length ? '' : ts.length === 1 ? ts[0]! : `${ts[0]} +${ts.length - 1}`

/** A column header that sorts the list: first click by the column's natural order, then flips. */
function SortHead({ col, label, sort, setSort, num }: { col: Sort['by']; label: string; sort: Sort; setSort: (s: Sort) => void; num?: boolean }) {
  const on = sort.by === col
  return (
    <span role="columnheader" className={num ? 'num' : undefined} aria-sort={on ? (sort.desc ? 'descending' : 'ascending') : 'none'}>
      <button
        className="rules-sort"
        onClick={() => setSort(on ? { by: col, desc: !sort.desc } : { by: col, desc: col === 'claims' })}
      >
        {label}
        <span aria-hidden className="rules-sort-mark">{on ? (sort.desc ? '↓' : '↑') : ''}</span>
      </button>
    </span>
  )
}

const PAGE = 50

/** Shows the first 50, then 50 more at a time: a payer list can run to hundreds. */
function usePaged<T>(rows: T[], reset: unknown, startsBlock?: (r: T) => boolean) {
  const [n, setN] = useState(PAGE)
  useEffect(() => setN(PAGE), [reset])
  // A page never ends inside a payer's block: its changes stay with it.
  let end = Math.min(n, rows.length)
  if (startsBlock) while (end < rows.length && !startsBlock(rows[end]!)) end++
  return {
    rows: rows.slice(0, end),
    more:
      rows.length > end ? (
        <button className="btn rules-showmore" onClick={() => setN(end + PAGE)}>
          Show {int(Math.min(PAGE, rows.length - end))} more
        </button>
      ) : null,
  }
}

/* ------------------------------------------------------------------ a payer's block (#597) */

/**
 * Where a line sits in its payer's block (#597, Yehuda 2026-10-14). `main`: the payer's
 * own line, with what is in force when the data starts; `state`: one state's line (↳ NY
 * only); `sub`: a change from a date after the data starts, or one not saved yet;
 * `history`: a setting replaced before the data starts, which applies to no claim,
 * folded under "History (n)".
 */
type Role = 'main' | 'state' | 'sub' | 'history'

interface Placed {
  state: string | null
  from: string | null
  role: Role
  /** On a main or state line: how many settings its History folds. */
  nHistory: number
}

/**
 * One payer's lines, in order: its own line, its history and its later changes, then
 * each state's line likewise (splitAtDataStart). `rows` are the payer's rows, switched
 * off ones too (an undated state row switched off still lists, to switch back on);
 * `pending` the lines picked from the menu but not saved yet.
 */
function placeBlock(
  rows: readonly { active: boolean; state: string | null; effectiveFrom: string | null }[],
  pending: readonly { state: string | null; from: string | null }[],
  dataStart: string | null,
): Placed[] {
  const states = [
    ...new Set([
      ...rows.filter((r) => r.state && (r.active || !r.effectiveFrom)).map((r) => r.state!.toUpperCase()),
      ...pending.filter((p) => p.state).map((p) => p.state!.toUpperCase()),
    ]),
  ].sort()
  const out: Placed[] = []
  for (const st of [null, ...states]) {
    const mine = rows.filter((r) => r.active && low(r.state) === low(st))
    const { head, later, history } = splitAtDataStart(mine, dataStart)
    const headFrom = head?.effectiveFrom ?? null
    out.push({ state: st, from: headFrom, role: st ? 'state' : 'main', nHistory: history.length })
    for (const h of history) out.push({ state: st, from: h.effectiveFrom, role: 'history', nHistory: 0 })
    const taken = new Set([headFrom, ...history.map((h) => h.effectiveFrom)])
    const froms = [
      ...later.map((r) => r.effectiveFrom!),
      ...pending.filter((p) => p.from && low(p.state) === low(st)).map((p) => p.from!),
    ]
    for (const f of [...new Set(froms)].filter((f) => !taken.has(f)).sort()) out.push({ state: st, from: f, role: 'sub', nHistory: 0 })
  }
  return out
}

/** A line under its payer, in words: "↳ From Mar 1, 2026", "↳ NY only, from Mar 1, 2026". */
function subLabel(l: { role: Role; state: string | null; from: string | null }): string {
  const st = l.state ? `${l.state} only` : ''
  const from = l.from ? `from ${shortDate(l.from)}` : l.role === 'history' ? 'all dates' : ''
  const words = [st, from].filter(Boolean).join(', ')
  return `↳ ${words.charAt(0).toUpperCase()}${words.slice(1)}`
}

/** A line's classes: default or set, under its payer, folded history, part of a block. */
function rowClass(role: Role, set: boolean, inBlock: boolean): string {
  return `rules-row${set ? '' : ' is-default'}${role === 'main' ? '' : ' is-sub'}${role === 'history' ? ' is-history' : ''}${inBlock ? ' in-block' : ''}`
}

/** Under a History line: why it counts for nothing, and how to tidy it away. */
function HistoryNote({ start }: { start: string | null }) {
  return (
    <span className="rules-sub">
      Replaced before the data starts{start ? ` (${shortDate(start)})` : ''}: applies to no claim. ⋯ → Remove this change.
    </span>
  )
}

/** "History (2)": shows or hides the settings a line replaced before the data starts. */
function HistoryToggle({ n, open, onToggle }: { n: number; open: boolean; onToggle: () => void }) {
  if (!n) return null
  return (
    <button type="button" className="rules-history-toggle" aria-expanded={open} onClick={onToggle}>
      {open ? '▾' : '▸'} History ({n})
    </button>
  )
}

/** Which blocks' History is open, by `payer|state`. */
function useFolds() {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  return {
    has: (k: string) => open.has(k),
    toggle: (k: string) => setOpen((s) => (s.has(k) ? new Set([...s].filter((x) => x !== k)) : new Set([...s, k]))),
  }
}

/** "a@x.com · Oct 6" for a rule's last change. */
function lastChange(r: { createdBy: string; createdAt: string; updatedBy: string | null; updatedAt: string | null }) {
  const by = (r.updatedBy ?? r.createdBy).split('@')[0]
  const at = new Date(r.updatedAt ?? r.createdAt)
  return `${by} · ${Number.isNaN(at.getTime()) ? '' : at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
}

/** A number field that saves when it loses focus or on Enter, and only if it changed. */
function NumberCell({
  value,
  onCommit,
  label,
  placeholder,
  dimmed,
  min = 0,
  max = 100,
  allowBlank = false,
}: {
  value: number | null
  onCommit: (v: number | null) => Promise<unknown> | void
  label: string
  placeholder?: string
  dimmed?: boolean
  min?: number
  max?: number
  allowBlank?: boolean
}) {
  const [text, setText] = useState(value === null ? '' : String(value))
  const [bad, setBad] = useState(false)
  useEffect(() => setText(value === null ? '' : String(value)), [value])
  const commit = () => {
    const t = text.trim()
    if (t === '') {
      if (!allowBlank) return setText(value === null ? '' : String(value))
      if (value !== null) void onCommit(null)
      return setBad(false)
    }
    const n = Number(t)
    if (!Number.isFinite(n) || n < min || n > max) return setBad(true)
    setBad(false)
    if (n !== value) void onCommit(n)
  }
  return (
    <input
      className={`rules-input rules-num${dimmed ? ' is-default' : ''}`}
      inputMode="decimal"
      aria-label={label}
      aria-invalid={bad || undefined}
      title={bad ? `A number from ${min} to ${max}` : undefined}
      placeholder={placeholder}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') {
          setText(value === null ? '' : String(value))
          setBad(false)
        }
      }}
    />
  )
}

/** A text field that saves on blur or Enter, if it changed. */
function TextCell({
  value,
  onCommit,
  label,
  placeholder,
  disabled,
}: {
  value: string
  onCommit: (v: string) => void
  label: string
  placeholder?: string
  disabled?: boolean
}) {
  const [text, setText] = useState(value)
  useEffect(() => setText(value), [value])
  return (
    <input
      className="rules-input"
      aria-label={label}
      placeholder={placeholder}
      maxLength={400}
      disabled={disabled}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => text.trim() !== value.trim() && onCommit(text.trim())}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') setText(value)
      }}
    />
  )
}

/** Plain words for a match value: "starts with HMO", "contains coins", "is Private Pay". */
function describeMatch(kind: 'exact' | 'pattern', value: string): ReactNode {
  if (kind === 'exact') return <>is <b>{value}</b></>
  const v = value.trim()
  const inner = v.replace(/^\*+|\*+$/g, '')
  if (!inner.includes('*')) {
    if (v.startsWith('*') && v.endsWith('*')) return <>contains <b>{inner}</b></>
    if (v.endsWith('*')) return <>starts with <b>{inner}</b></>
    if (v.startsWith('*')) return <>ends with <b>{inner}</b></>
  }
  return <>matches <b>{v}</b></>
}

/* ------------------------------------------------------------------ coinsurance billers */

function BillersTab({ rules, vocab, api, run, onHistory }: TabProps) {
  const ordered = sortBiller(rules.biller)
  const active = ordered.filter((r) => r.active)
  const off = ordered.filter((r) => !r.active)
  const [editing, setEditing] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  const preview = useMemo(
    () => (vocab.coinsCombos ? previewBiller(rules.biller, vocab.coinsCombos, false) : null),
    [rules.biller, vocab.coinsCombos],
  )
  const byRule = new Map(preview?.byRule.map((b) => [b.rule.id, b]))
  const top = active[0]?.priority ?? 100
  const bottom = active[active.length - 1]?.priority ?? 0

  const add = (s: BillerSentence[], first: boolean, what: string) =>
    run(what, async () => {
      // At the top: below every number in use. At the bottom: above it. Ten apart, so
      // there is room between them; moving a rule renumbers 10, 20, 30….
      let p = first ? Math.min(top, 10) - 10 * s.length : Math.max(bottom, 0) + 10
      for (const r of s) {
        await api.add('coinsurance_biller_rule', {
          ...r,
          matchKind: isPattern(r.matchValue) ? 'pattern' : 'exact',
          priority: p,
        })
        p += 10
      }
    })

  const move = (id: number, dir: -1 | 1) => {
    const changes = reorderBiller(rules.biller, id, dir)
    if (!changes.length) return
    setBusy(true)
    void run('Order saved.', async () => {
      for (const c of changes) await api.update('coinsurance_biller_rule', c.id, { priority: c.priority })
    }).finally(() => setBusy(false))
  }

  const suggestions = SUGGESTED_BILLER.filter((s) => !s.rules.every((r) => hasBillerRule(rules.biller, r)))

  return (
    <>
      <p className="body-note rules-lede">
        Rules are checked top to bottom; the first one that fits a coinsurance claim decides who gets the credit.
        <Info
          def={{
            label: 'How the preview counts',
            text:
              'Counts are the coinsurance claims you can see in Claim detail: claim type Co-Insurance, or a payer whose name contains “coins”. Claim detail has each claim’s own payer, not its primary payer, so the data model makes the final call.',
          }}
        />
      </p>

      {suggestions.length > 0 && (
        <div className="rules-suggest">
          {suggestions.map((s) => (
            <div key={s.id} className="rules-suggest-card">
              <span className="rules-tag">Suggested — not active until you add it</span>
              {s.rules.map((r, i) => (
                <p key={i} className="rules-sentence" style={{ margin: '6px 0 0' }}>
                  When the {r.matchField === 'payer_type' ? 'payer type' : 'payer'}{' '}
                  {describeMatch(isPattern(r.matchValue) ? 'pattern' : 'exact', r.matchValue)} → credit the coinsurance
                  to <b>{creditLabel(r.creditedDepartment)}</b>
                </p>
              ))}
              <p className="dim" style={{ margin: '6px 0 10px', fontSize: 12 }}>
                {s.why} Requested 2026-10-05.{s.first ? ' Goes to the top, so it wins over the HMO rule.' : ''}
              </p>
              <button
                className="btn rules-primary"
                disabled={busy}
                onClick={() =>
                  void add(
                    s.rules.filter((r) => !hasBillerRule(rules.biller, r)),
                    s.first,
                    s.rules.length > 1 ? 'Rules added.' : 'Rule added.',
                  )
                }
              >
                Add {s.rules.length > 1 ? 'these rules' : 'this rule'}
              </button>
            </div>
          ))}
        </div>
      )}

      <ol className="rules-list rules-billers" aria-label="Coinsurance biller rules, in order">
        {active.length === 0 && (
          <li className="empty-note" style={{ listStyle: 'none' }}>
            No rules yet: every coinsurance claim stays with the department it is billed under.
          </li>
        )}
        {active.map((r, i) =>
          editing === r.id ? (
            <li key={r.id} className="rules-brow">
              <SentenceForm
                vocab={vocab}
                initial={r}
                submitLabel="Save"
                onCancel={() => setEditing(null)}
                onSubmit={async (s, note) => {
                  const body: Record<string, unknown> = {
                    ...s,
                    matchKind: isPattern(s.matchValue) ? 'pattern' : 'exact',
                    note: note || null,
                  }
                  const patch = Object.fromEntries(
                    Object.entries(body).filter(([k, v]) => (r as unknown as Record<string, unknown>)[k] !== v),
                  )
                  if (!Object.keys(patch).length) return setEditing(null)
                  if (await run('Rule saved.', () => api.update('coinsurance_biller_rule', r.id, patch))) setEditing(null)
                }}
              />
            </li>
          ) : (
            <li key={r.id} className="rules-brow">
              <span className="rules-order" aria-hidden>
                {i + 1}
              </span>
              <div style={{ minWidth: 0 }}>
                <p className="rules-sentence">
                  When the {r.matchField === 'payer_type' ? 'payer type' : 'payer'} {describeMatch(r.matchKind, r.matchValue)} →
                  credit the coinsurance to <b>{creditLabel(r.creditedDepartment)}</b>
                  {datesNote(r) && <span className="dim">{datesNote(r)}</span>}
                </p>
                <BillerPreviewLine b={byRule.get(r.id)} alone={vocab.coinsCombos ? claimsForBillerRule(vocab.coinsCombos, r) : null} loading={!preview} />
                {r.note && <p className="dim rules-note">{r.note}</p>}
              </div>
              <div className="rules-acts">
                <button className="btn rules-icon" aria-label="Move up" title="Move up" disabled={busy || i === 0} onClick={() => move(r.id, -1)}>
                  ↑
                </button>
                <button
                  className="btn rules-icon"
                  aria-label="Move down"
                  title="Move down"
                  disabled={busy || i === active.length - 1}
                  onClick={() => move(r.id, 1)}
                >
                  ↓
                </button>
                <button className="btn" onClick={() => setEditing(r.id)}>
                  Edit
                </button>
                <Menu
                  label="More"
                  items={[
                    { label: 'History', onClick: () => onHistory({ table: 'coinsurance_biller_rule', id: r.id, title: billerTitle(r) }) },
                    {
                      label: 'Switch off',
                      onClick: () => void run('Rule switched off.', () => api.update('coinsurance_biller_rule', r.id, { active: false })),
                    },
                  ]}
                />
              </div>
            </li>
          ),
        )}
        <li className="rules-brow rules-add">
          <SentenceForm
            vocab={vocab}
            submitLabel="Add rule"
            lead="Add a rule:"
            onSubmit={async (s, note) => {
              let ok = false
              ok = await run('Rule added.', () =>
                api.add('coinsurance_biller_rule', {
                  ...s,
                  matchKind: isPattern(s.matchValue) ? 'pattern' : 'exact',
                  priority: Math.max(bottom, 0) + 10,
                  note: note || null,
                }),
              )
              return ok
            }}
          />
        </li>
      </ol>

      {preview && active.length > 0 && (
        <p className="dim" style={{ fontSize: 12, marginTop: 8 }}>
          {preview.unmatched === 0
            ? `Every one of the ${plural(preview.considered, 'coinsurance claim')} you can see fits a rule.`
            : `No rule fits ${plural(preview.unmatched, 'coinsurance claim')} of ${int(preview.considered)}; they stay with the department they are billed under.`}
        </p>
      )}
      {!preview && vocab.claimsError && <p className="dim" style={{ fontSize: 12 }}>Counts unavailable: {vocab.claimsError}</p>}

      {off.length > 0 && (
        <details className="rules-off">
          <summary>Switched off ({off.length})</summary>
          <ul className="rules-list">
            {off.map((r) => (
              <li key={r.id} className="rules-brow is-off">
                <p className="rules-sentence" style={{ minWidth: 0 }}>
                  When the {r.matchField === 'payer_type' ? 'payer type' : 'payer'} {describeMatch(r.matchKind, r.matchValue)} →
                  credit the coinsurance to <b>{creditLabel(r.creditedDepartment)}</b>
                  {datesNote(r) && <span className="dim">{datesNote(r)}</span>}
                </p>
                <div className="rules-acts">
                  <button
                    className="btn"
                    onClick={() =>
                      void run('Rule switched on.', () =>
                        api.update('coinsurance_biller_rule', r.id, { active: true, priority: Math.max(bottom, 0) + 10 }),
                      )
                    }
                  >
                    Switch on
                  </button>
                  <button className="btn" onClick={() => onHistory({ table: 'coinsurance_biller_rule', id: r.id, title: billerTitle(r) })}>
                    History
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  )
}

const billerTitle = (r: BillerRule) => `${r.matchValue} → ${creditLabel(r.creditedDepartment)}`

/** "Catches 1,234 claims; 900 would move to Medicare (850 from HMO…)". */
function BillerPreviewLine({ b, alone, loading }: { b: BillerPreview | undefined; alone: number | null; loading: boolean }) {
  if (loading) return <p className="rules-preview dim">Counting claims…</p>
  if (!b) return null
  const move = b.claims - b.alreadyThere
  const from = Object.entries(b.from)
    .sort((x, y) => y[1] - x[1])
    .map(([d, n]) => `${int(n)} from ${d || 'no department'}`)
  const shadowed = alone !== null && alone > b.claims ? alone - b.claims : 0
  return (
    <p className="rules-preview">
      {b.claims === 0 ? (
        <span className="dim">Catches no coinsurance claims you can see</span>
      ) : (
        <>
          Catches <b>{plural(b.claims, 'claim')}</b>
          {isPrimaryCredit(b.rule.creditedDepartment) ? (
            <span className="dim">; each goes to its primary payer’s department, which the model decides</span>
          ) : move > 0 ? (
            <>
              ; <b>{int(move)}</b> would move to {b.rule.creditedDepartment}
              {from.length > 0 && <span className="dim"> ({from.join(', ')})</span>}
            </>
          ) : (
            <span className="dim">; all already with {b.rule.creditedDepartment}</span>
          )}
        </>
      )}
      {shadowed > 0 && <span className="dim"> · {int(shadowed)} more fit but a rule above takes them</span>}
    </p>
  )
}

/**
 * "When the [payer type ▾] [is ▾ HMO/Private Insurance] → credit the coinsurance to
 * [Medicare ▾]", as controls. A value with * is a pattern.
 */
function SentenceForm({
  vocab,
  initial,
  submitLabel,
  lead,
  onSubmit,
  onCancel,
}: {
  vocab: Vocab
  initial?: BillerRule
  submitLabel: string
  lead?: string
  onSubmit: (s: BillerSentence, note: string) => Promise<unknown>
  onCancel?: () => void
}) {
  const blank: BillerSentence = { matchField: 'payer_type', matchValue: '', creditedDepartment: 'Medicare', effectiveFrom: null, effectiveTo: null }
  const [s, setS] = useState<BillerSentence>(
    initial
      ? {
          matchField: initial.matchField,
          matchValue: initial.matchValue,
          creditedDepartment: initial.creditedDepartment,
          effectiveFrom: initial.effectiveFrom,
          effectiveTo: initial.effectiveTo,
        }
      : blank,
  )
  const badDates = !!s.effectiveFrom && !!s.effectiveTo && s.effectiveTo < s.effectiveFrom
  const [note, setNote] = useState(initial?.note ?? '')
  const [saving, setSaving] = useState(false)
  const options = s.matchField === 'payer_type' ? vocab.payerTypes : vocab.payers
  const withCurrent = s.matchValue && !options.some((o) => o.value === s.matchValue) ? [{ value: s.matchValue, label: s.matchValue }, ...options] : options
  const depts = s.creditedDepartment && !vocab.departments.includes(s.creditedDepartment) ? [s.creditedDepartment, ...vocab.departments] : vocab.departments
  return (
    <form
      className="rules-sentence-form"
      onSubmit={(e) => {
        e.preventDefault()
        if (!s.matchValue.trim()) return
        setSaving(true)
        void onSubmit(s, note.trim())
          .then((ok) => {
            if (ok !== false && !initial) {
              setS(blank)
              setNote('')
            }
          })
          .finally(() => setSaving(false))
      }}
    >
      {lead && <span className="rules-lead">{lead}</span>}
      <span className="rules-word">When the</span>
      <select
        className="rules-input rules-select"
        aria-label="Match on"
        value={s.matchField}
        onChange={(e) => setS({ ...s, matchField: e.target.value as BillerSentence['matchField'], matchValue: '' })}
      >
        <option value="payer_type">payer type</option>
        <option value="payer">payer</option>
      </select>
      <span className="rules-word">is</span>
      <div className="rules-pick">
        <SearchPick
          label={s.matchField === 'payer_type' ? 'Payer type' : 'Payer'}
          quietLabel
          value={s.matchValue}
          options={withCurrent}
          onChange={(v) => setS({ ...s, matchValue: v })}
          placeholder={s.matchField === 'payer_type' ? 'Pick a payer type…' : 'Pick a payer…'}
          custom={(q) => (q.trim() ? { value: q.trim(), label: `Use “${q.trim()}”${isPattern(q) ? ' (* = any text)' : ''}` } : null)}
        />
      </div>
      <span className="rules-word">→ credit the coinsurance to</span>
      <select
        className="rules-input rules-select"
        aria-label="Credit to"
        value={s.creditedDepartment}
        onChange={(e) => setS({ ...s, creditedDepartment: e.target.value })}
      >
        {depts.filter((d) => !isPrimaryCredit(d)).map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
        <option value={PRIMARY_PAYER}>{creditLabel(PRIMARY_PAYER)}</option>
      </select>
      {/* By the claim's bill date; blank = no limit. */}
      <span className="rules-word">from</span>
      <input
        type="date"
        className="rules-input rules-date"
        min={MIN_DAY}
        max={MAX_DAY}
        aria-label="In force from (optional)"
        value={s.effectiveFrom ?? ''}
        onChange={(e) => setS({ ...s, effectiveFrom: e.target.value || null })}
      />
      <span className="rules-word">until</span>
      <input
        type="date"
        className="rules-input rules-date"
        min={MIN_DAY}
        max={MAX_DAY}
        aria-label="In force until (optional)"
        aria-invalid={badDates}
        value={s.effectiveTo ?? ''}
        onChange={(e) => setS({ ...s, effectiveTo: e.target.value || null })}
      />
      <input
        className="rules-input rules-notefield"
        aria-label="Note (optional)"
        placeholder="Note (optional)"
        maxLength={400}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <span style={{ display: 'inline-flex', gap: 6 }}>
        <button className="btn rules-primary" type="submit" disabled={saving || !s.matchValue.trim() || badDates}>
          {saving ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button className="btn" type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
      </span>
    </form>
  )
}

/* ------------------------------------------------------------------ coinsurance payers */

/** One line of the coinsurance payer list: a payer (maybe one state, maybe from a date), set or not. */
interface PayerLine {
  key: string
  payer: string
  state: string | null
  /** A change in force from this bill date; null for the line that always applies. */
  from: string | null
  claims: number
  byDept: Record<string, number>
  row: CoinsurancePayer | null
  /** Its place in the payer's block (#597). */
  role: Role
  nHistory: number
  /** The payer: lines of one block share it. */
  block: string
  /** The block's History key: payer and state. */
  fold: string
}

const PAYER_DEFAULTS = { weight: 1, includeForHmo: true, includeForMedicare: true }

function PayersTab({ rules, vocab, api, run, onHistory }: TabProps) {
  const [q, setQ] = useState('')
  const [only, setOnly] = useState(false)
  const [filter, setFilter] = useState<PayerFilter>({ types: [], payers: [] })
  const typesOf = useMemo(() => typesByPayer(vocab.totals), [vocab.totals])
  const [sort, setSort] = useState<Sort>(BY_CLAIMS)
  const [stateFor, setStateFor] = useState<string | null>(null)
  const [dateFor, setDateFor] = useState<string | null>(null)
  // A dated line picked from the menu but not saved yet: saved once a value on it changes (#584).
  const [pending, setPending] = useState<{ payer: string; state: string | null; from: string }[]>([])

  const lines = useMemo(() => {
    const rows = rules.payers
    const byName = new Map<string, PayerTotal>()
    for (const t of vocab.totals ?? []) if (t.coinsurance > 0) byName.set(low(t.payer), t)
    const out: PayerLine[] = []
    const seen = new Set<string>()
    const push = (payer: string, p: Placed) => {
      const { state, from } = p
      const key = `${low(payer)}|${low(state)}|${from ?? ''}`
      if (seen.has(key)) return
      seen.add(key)
      const t = byName.get(low(payer))
      out.push({
        key,
        payer,
        state,
        from,
        claims: t?.coinsurance ?? 0,
        byDept: t?.coinsByDept ?? {},
        row: rows.find((r) => low(r.payerName) === low(payer) && low(r.state) === low(state) && (r.effectiveFrom ?? null) === from) ?? null,
        role: p.role,
        nHistory: p.nHistory,
        block: low(payer),
        fold: `${low(payer)}|${low(state)}`,
      })
    }
    // The data's coinsurance payers and any set payer the data lacks (0 claims), in the
    // chosen order — by coinsurance claims unless a header says otherwise. Each payer's
    // one-state settings stay right under it.
    const all = [...byName.values()].map((t) => ({ payer: t.payer, claims: t.coinsurance }))
    for (const r of rows) {
      if (!byName.has(low(r.payerName)) && !all.some((x) => low(x.payer) === low(r.payerName)))
        all.push({ payer: r.payerName, claims: 0 })
    }
    const names = sortPayers(all, sort, (x) => x.payer, (x) => x.claims, (x) => (typesOf.get(low(x.payer)) ?? []).join(', ')).map((x) => x.payer)
    // Each payer is one block: its own line with what is in force when the data starts,
    // then its history (folded), its later changes and its one-state lines (#597).
    for (const name of names) {
      const mine = rows.filter((r) => low(r.payerName) === low(name))
      for (const p of placeBlock(mine, pending.filter((x) => low(x.payer) === low(name)), vocab.dataStart)) push(name, p)
    }
    return out
  }, [rules.payers, vocab.totals, vocab.dataStart, sort, typesOf, pending])

  const isPending = (l: PayerLine) =>
    !!l.from && !l.row?.active && pending.some((p) => low(p.payer) === low(l.payer) && low(p.state) === low(l.state) && p.from === l.from)
  const unpick = (l: PayerLine) =>
    setPending((p) => p.filter((x) => !(low(x.payer) === low(l.payer) && low(x.state) === low(l.state) && x.from === l.from)))
  /** What a line not saved yet starts as: what is in force before its date, or the all-states line for a state. */
  const startOf = (l: PayerLine) => {
    const base = l.from
      ? settingBefore(rules.payers, l.payer, l.state, l.from)
      : l.state
        ? (lines.find((x) => x.block === l.block && x.role === 'main')?.row ?? null)
        : null
    return base?.active ? { weight: base.weight, includeForHmo: base.includeForHmo, includeForMedicare: base.includeForMedicare } : PAYER_DEFAULTS
  }

  // Filters and the search pick payers, so a block shows whole or not at all (#597).
  const folds = useFolds()
  const blocks = new Map<string, boolean>()
  for (const l of lines) {
    const pass =
      (!only || !!l.row?.active || isPending(l)) &&
      (!q.trim() || low(l.payer).includes(low(q)) || low(l.state) === low(q)) &&
      payerPasses(filter, l.payer, typesOf)
    blocks.set(l.block, (blocks.get(l.block) ?? false) || pass)
  }
  const filtered = lines.filter((l) => blocks.get(l.block) && (l.role !== 'history' || folds.has(l.fold)))
  const inBlock = new Set(lines.filter((l) => l.role !== 'main').map((l) => l.block))
  const paged = usePaged(filtered, `${q}|${only}|${filter.types.join()}|${filter.payers.join()}`, (l) => l.role === 'main')
  // Bulk actions act on payers: only a payer's own line has a box.
  const mains = useMemo(() => lines.filter((l) => l.role === 'main'), [lines])
  const sel = useSelection(mains, filtered.filter((l) => l.role === 'main'))
  const picked = () => lines.filter((l) => sel.has(l.key))
  const lineName = (l: PayerLine) => `${l.payer}${l.state ? ` (${l.state})` : ''}${l.from ? ` from ${shortDate(l.from)}` : ''}`

  const bulkWeight = (w: number | null, day: string | null) => {
    if (w === null) return
    const ls = picked()
    // One row per payer, state and start date: a payer's line and its dated lines moved
    // to one date are the same setting (#399).
    const rows = new Map<string, Record<string, string>>()
    for (const l of ls) {
      const from = day ?? l.from
      const k = `${low(l.payer)}|${low(l.state)}|${from ?? ''}`
      if (rows.has(k)) continue
      // A new dated setting starts from what the line says today, then takes the weight.
      const base = day && !l.from && l.row?.active ? l.row : null
      rows.set(k, {
        payerName: l.payer,
        ...(l.state ? { state: l.state } : {}),
        ...(from ? { effectiveFrom: from } : {}),
        ...(base ? { includeForHmo: String(base.includeForHmo), includeForMedicare: String(base.includeForMedicare) } : {}),
        weight: String(w),
        active: 'true',
      })
    }
    let summary = ''
    void run(
      () => summary,
      async () => {
        const r = await bulkSave(api, 'coinsurance_payer', [...rows.values()])
        summary = `Weight ${w}${day ? ` from ${shortDate(day)}` : ''} on ${plural(ls.length, 'payer')}: ${importSummary(r)}`
      },
    ).then((ok) => ok && sel.clear())
  }
  const bulkReset = () => {
    const ls = picked().filter((l) => l.row?.active)
    if (!ls.length) return
    void run(`${plural(ls.length, 'payer')} reset to default.`, () =>
      bulkSave(api, 'coinsurance_payer', ls.map((l) => ({ id: String(l.row!.id), active: 'false' }))),
    ).then((ok) => ok && sel.clear())
  }

  const save = async (l: PayerLine, change: Partial<typeof PAYER_DEFAULTS>) => {
    const r = l.row
    // A line not saved (or switched off) starts from what is in force there, then takes the change.
    const values = { ...(r?.active ? {} : startOf(l)), ...change }
    if (r) {
      const patch: Record<string, unknown> = Object.fromEntries(
        Object.entries(values).filter(([k, v]) => (r as unknown as Record<string, unknown>)[k] !== v),
      )
      if (!r.active) patch.active = true
      if (!Object.keys(patch).length) return
      return run(`${lineName(l)} saved.`, () => api.update('coinsurance_payer', r.id, patch)).then((ok) => ok && unpick(l))
    }
    return run(`${lineName(l)} saved.`, () =>
      api.add('coinsurance_payer', { payerName: l.payer, state: l.state, ...(l.from ? { effectiveFrom: l.from } : {}), ...values }),
    ).then((ok) => ok && unpick(l))
  }

  /**
   * A change from a bill date: a line under the payer showing what is in force then,
   * saved only once a value on it changes. Saving the copy straight away made a second
   * line that changed nothing (#584).
   */
  const addDated = (l: PayerLine, day: string) => {
    setDateFor(null)
    setPending((p) => [...p, { payer: l.payer, state: l.state, from: day }])
  }

  const addState = (l: PayerLine, st: string) => {
    setStateFor(null)
    const existing = rules.payers.find((r) => low(r.payerName) === low(l.payer) && low(r.state) === low(st) && !r.effectiveFrom)
    const values = l.row?.active
      ? { weight: l.row.weight, includeForHmo: l.row.includeForHmo, includeForMedicare: l.row.includeForMedicare }
      : PAYER_DEFAULTS
    void run(`${l.payer} (${st}) added.`, () =>
      existing
        ? api.update('coinsurance_payer', existing.id, { active: true })
        : api.add('coinsurance_payer', { payerName: l.payer, state: st, ...values }),
    )
  }

  const setCount = rules.payers.filter((r) => r.active).length
  const exportRows: CoinsPayerExportRow[] = filtered.map((l) => {
    const set = !!l.row?.active
    const unsaved = isPending(l)
    const v = set ? l.row! : unsaved ? startOf(l) : PAYER_DEFAULTS
    const main = l.role === 'main'
    return {
      payer: l.payer,
      role: l.role,
      state: l.state,
      from: l.from,
      payerTypes: typesOf.get(low(l.payer)) ?? [],
      claims: l.role === 'history' ? 0 : vocab.days ? lineClaims(vocab.days, { kind: 'exact', ...l, from: main ? null : l.from }, true) : null,
      includeForHmo: v.includeForHmo,
      includeForMedicare: v.includeForMedicare,
      weight: v.weight,
      status: set ? 'Set' : unsaved ? 'Not saved' : 'Default',
    }
  })

  return (
    <>
      <p className="body-note rules-lede">
        Every payer on coinsurance claims in the data, most claims first. Change a box or a weight and it is saved as
        a rule; payers you leave alone count as normal claims.
        <Info
          def={{
            label: 'Which payers are listed',
            text:
              'Payers on claims of type Co-Insurance, or whose name contains “coins”, among the claims you can see in Claim detail. A coinsurance line carries the payer billed for it, often a secondary payer, so Medicaid and Medicare plans appear here too. Payers you set that are not in the data are listed with 0 claims.',
          }}
        />
      </p>
      <DatesNote />
      <Toolbar
        q={q}
        setQ={setQ}
        only={only}
        setOnly={setOnly}
        onlyLabel={`Only payers I’ve set (${setCount})`}
        shown={filtered.length}
        total={lines.length}
        noun="payer"
      >
        <PayerFilters value={filter} onChange={setFilter} totals={vocab.totals ?? []} />
      </Toolbar>
      <SelectBar sel={sel} shownCount={filtered.length} noun="payer">
        <BulkWeight label="Weight" min={0} onApply={bulkWeight} />
        <button type="button" className="btn" onClick={bulkReset}>
          Reset to default
        </button>
      </SelectBar>
      {!vocab.totals && (
        <p className="dim" style={{ fontSize: 12 }}>
          {vocab.claimsError ? `The payers in the data are unavailable: ${vocab.claimsError}` : 'Loading the payers in the data…'}
        </p>
      )}
      <ExportBar filename="rules-coinsurance-payers.csv" rows={exportRows} columns={COINS_PAYER_EXPORT} />
      <div className="rules-grid rules-payers" role="table" aria-label="Coinsurance payers">
        <div className="rules-row head" role="row">
          <SelectAllBox sel={sel} label="Select every payer shown" />
          <SortHead col="payer" label="Payer" sort={sort} setSort={setSort} />
          <SortHead col="type" label="Payer type" sort={sort} setSort={setSort} />
          <SortHead col="claims" label="Claims" sort={sort} setSort={setSort} num />
          <span role="columnheader">
            Counts for HMO
            <Info def={{ label: 'Counts for HMO', text: 'Off: this payer’s coinsurance claims count 0 for HMO billers (only when weighted claims are on).' }} />
          </span>
          <span role="columnheader">
            Counts for Medicare
            <Info def={{ label: 'Counts for Medicare', text: 'Off: this payer’s coinsurance claims count 0 for Medicare billers (only when weighted claims are on).' }} />
          </span>
          <span role="columnheader">
            Weight
            <Info def={{ label: 'Weight', text: 'What one claim counts as: 1 is a normal claim, 0.5 is half, 0 counts nothing.' }} />
          </span>
          <span role="columnheader" aria-label="Status" />
          <span role="columnheader" aria-label="More" />
        </div>
        {filtered.length === 0 && (
          <div className="empty-note" role="row">
            {only ? 'You haven’t set any coinsurance payers yet.' : q ? 'No payer matches that search.' : 'No coinsurance payers in the data.'}
          </div>
        )}
        {paged.rows.map((l) => {
          const set = !!l.row?.active
          const unsaved = isPending(l)
          const v = set ? l.row! : unsaved ? startOf(l) : PAYER_DEFAULTS
          const noChange = l.role === 'sub' && set && changesNothing(rules.payers, l.row!, ['weight', 'includeForHmo', 'includeForMedicare'], PAYER_DEFAULTS)
          const main = l.role === 'main'
          const label = main ? l.payer : lineName(l)
          const deptNote = Object.entries(l.byDept)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([d, n]) => `${d || '—'} ${int(n)}`)
            .join(' · ')
          return (
            <div key={l.key} role="row" className={rowClass(l.role, set, inBlock.has(l.block))}>
              <span role="cell" className="rules-pick-cell">
                {main && <input type="checkbox" aria-label={`Select ${l.payer}`} checked={sel.has(l.key)} onChange={() => sel.toggle(l.key)} />}
              </span>
              <span role="cell" className="rules-name">
                {main ? <span className="rules-payer">{l.payer}</span> : <span className="rules-subline">{subLabel(l)}</span>}
                {main && l.from && <span className="rules-sub">In force from {shortDate(l.from)}</span>}
                {deptNote && main && <span className="rules-sub">Billed today: {deptNote}</span>}
                <HistoryToggle n={l.nHistory} open={folds.has(l.fold)} onToggle={() => folds.toggle(l.fold)} />
                {l.role === 'history' && <HistoryNote start={vocab.dataStart} />}
                {unsaved && <span className="rules-sub">Change a value to save this change</span>}
                {noChange && (
                  <span className="rules-sub rules-warn">
                    Same as before {shortDate(l.from!)}: changes nothing. Change a value, or ⋯ → Remove this change.
                  </span>
                )}
              </span>
              <span role="cell" className="dim rules-type" data-label="Payer type" title={(typesOf.get(low(l.payer)) ?? []).join(', ')}>
                {main ? typesText(typesOf.get(low(l.payer))) : ''}
              </span>
              <span role="cell" className="num rules-claims" data-label="Claims">
                {l.role === 'history' ? '0' : vocab.days ? int(lineClaims(vocab.days, { kind: 'exact', ...l, from: main ? null : l.from }, true)) : '…'}
              </span>
              <label role="cell" className="rules-cb" data-label="HMO">
                <input
                  type="checkbox"
                  checked={v.includeForHmo}
                  aria-label={`${label}: counts for HMO`}
                  onChange={(e) => void save(l, { includeForHmo: e.target.checked })}
                />
                <span className="rules-cb-text">Counts for HMO</span>
              </label>
              <label role="cell" className="rules-cb" data-label="Medicare">
                <input
                  type="checkbox"
                  checked={v.includeForMedicare}
                  aria-label={`${label}: counts for Medicare`}
                  onChange={(e) => void save(l, { includeForMedicare: e.target.checked })}
                />
                <span className="rules-cb-text">Counts for Medicare</span>
              </label>
              <span role="cell" data-label="Weight">
                <NumberCell
                  value={v.weight}
                  dimmed={!set}
                  label={`${label}: weight`}
                  onCommit={(n) => save(l, { weight: n ?? 1 })}
                />
              </span>
              <span role="cell" className="rules-status">
                {set ? (
                  <span title={`Last change: ${lastChange(l.row!)}`} className="rules-set">Set</span>
                ) : (
                  <span className="dim">{unsaved ? 'Not saved' : 'Default'}</span>
                )}
              </span>
              <span role="cell" className="rules-menu-cell">
                {dateFor === l.key ? (
                  <DayPick label="Change from (bill date)" onPick={(d) => addDated(l, d)} onCancel={() => setDateFor(null)} />
                ) : stateFor === l.key ? (
                  <select
                    className="rules-input"
                    autoFocus
                    aria-label="State"
                    defaultValue=""
                    onBlur={() => setStateFor(null)}
                    onChange={(e) => e.target.value && addState(l, e.target.value)}
                  >
                    <option value="">State…</option>
                    {vocab.states
                      .filter((s) => !rules.payers.some((r) => r.active && low(r.payerName) === low(l.payer) && low(r.state) === low(s)))
                      .map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                  </select>
                ) : (
                  <Menu
                    label="More"
                    items={[
                      {
                        label: 'History',
                        disabled: !l.row,
                        onClick: () => l.row && onHistory({ table: 'coinsurance_payer', id: l.row.id, title: l.payer + (l.state ? ` (${l.state})` : '') }),
                      },
                      ...(main ? [{ label: 'Set differently for one state…', onClick: () => setStateFor(l.key) }] : []),
                      ...(main || l.role === 'state' ? [{ label: 'Change from a date…', onClick: () => setDateFor(l.key) }] : []),
                      {
                        label: l.from || l.role === 'history' ? 'Remove this change' : l.state ? 'Remove this state’s setting' : 'Reset to default',
                        disabled: !set && !unsaved,
                        onClick: () =>
                          set
                            ? void run(`${lineName(l)} ${l.from ? 'removed' : 'reset to default'}.`, () => api.update('coinsurance_payer', l.row!.id, { active: false }))
                            : unpick(l),
                      },
                    ]}
                  />
                )}
              </span>
            </div>
          )
        })}
      </div>
      {paged.more}
    </>
  )
}

/* ------------------------------------------------------------------ hard payers */

interface HardLine {
  key: string
  payer: string
  /** One facility state's setting under its payer; null for the all-states line. */
  state: string | null
  /** A change in force from this bill date; null for the line that always applies. */
  from: string | null
  claims: number | null
  pattern: boolean
  row: HardPayer | null
  /** Its place in the payer's block (#597). */
  role: Role
  nHistory: number
  /** Match kind and payer: lines of one block share it. */
  block: string
  /** The block's History key: kind, payer and state. */
  fold: string
}

function HardTab({ rules, vocab, api, run, onHistory }: TabProps) {
  const [q, setQ] = useState('')
  const [only, setOnly] = useState(false)
  const [sort, setSort] = useState<Sort>(BY_CLAIMS)
  const [stateFor, setStateFor] = useState<string | null>(null)
  const [filter, setFilter] = useState<PayerFilter>({ types: [], payers: [] })
  const typesOf = useMemo(() => typesByPayer(vocab.totals), [vocab.totals])
  const [dateFor, setDateFor] = useState<string | null>(null)
  // A state or dated line picked from the menu but not saved yet: saved with its first weight.
  const [pending, setPending] = useState<{ payer: string; state: string | null; from: string | null }[]>([])

  const lines = useMemo(() => {
    const out: HardLine[] = []
    const rowFor = (kind: 'exact' | 'pattern', payer: string, state: string | null, from: string | null) =>
      rules.hard.find(
        (r) =>
          r.matchKind === kind &&
          low(r.payerName) === low(payer) &&
          low(r.state) === low(state) &&
          (r.effectiveFrom ?? null) === from,
      ) ?? null
    const line = (kind: 'exact' | 'pattern', payer: string, p: Placed): HardLine => ({
      key: `${kind}|${low(payer)}|${low(p.state)}|${p.from ?? ''}`,
      payer,
      state: p.state,
      from: p.from,
      claims: null,
      pattern: kind === 'pattern',
      row: rowFor(kind, payer, p.state, p.from),
      role: p.role,
      nHistory: p.nHistory,
      block: `${kind}|${low(payer)}`,
      fold: `${kind}|${low(payer)}|${low(p.state)}`,
    })
    /**
     * A payer's block (#597): its own line, with what is in force when the data starts,
     * then its history (folded), its later changes and its one-state lines. Saved ones
     * (active) and picked ones (not saved yet).
     */
    const block = (kind: 'exact' | 'pattern', payer: string, claims: number | null) => {
      const mine = rules.hard.filter((r) => r.matchKind === kind && r.active && low(r.payerName) === low(payer))
      const pend = kind === 'exact' ? pending.filter((p) => low(p.payer) === low(payer)) : []
      return placeBlock(mine, pend, vocab.dataStart).map((p) => ({ ...line(kind, payer, p), claims: p.role === 'main' ? claims : null }))
    }
    // Pattern rules first (from a CSV or the old page): they are not one payer.
    const patterns = [...new Set(rules.hard.filter((x) => x.matchKind === 'pattern' && x.active).map((x) => x.payerName))]
    for (const p of patterns) out.push(...block('pattern', p, vocab.combos ? claimsMatching(vocab.combos, 'pattern', p) : null))
    const exact = rules.hard.filter((x) => x.matchKind === 'exact')
    const seen = new Set<string>()
    const named: { payer: string; claims: number | null }[] = []
    for (const t of vocab.totals ?? []) {
      seen.add(low(t.payer))
      named.push({ payer: t.payer, claims: t.claims })
    }
    for (const r of exact)
      if (!seen.has(low(r.payerName))) {
        seen.add(low(r.payerName))
        named.push({ payer: r.payerName, claims: vocab.totals ? 0 : null })
      }
    for (const n of sortPayers(named, sort, (l) => l.payer, (l) => l.claims, (l) => (typesOf.get(low(l.payer)) ?? []).join(', ')))
      out.push(...block('exact', n.payer, n.claims))
    return out
  }, [rules.hard, vocab.totals, vocab.combos, vocab.dataStart, sort, pending, typesOf])

  // Filters and the search pick payers, so a block shows whole or not at all (#597).
  const folds = useFolds()
  const blocks = new Map<string, boolean>()
  for (const l of lines) {
    const pass =
      (!only || !!l.row?.active || l.role !== 'main') &&
      (!q.trim() || low(l.payer).includes(low(q)) || low(l.state) === low(q)) &&
      payerPasses(filter, l.payer, typesOf)
    blocks.set(l.block, (blocks.get(l.block) ?? false) || pass)
  }
  const filtered = lines.filter((l) => blocks.get(l.block) && (l.role !== 'history' || folds.has(l.fold)))
  const inBlock = new Set(lines.filter((l) => l.role !== 'main').map((l) => l.block))
  const paged = usePaged(filtered, `${q}|${only}|${filter.types.join()}|${filter.payers.join()}`, (l) => l.role === 'main')
  // Bulk actions act on payers: only a payer's own line has a box.
  const mains = useMemo(() => lines.filter((l) => l.role === 'main'), [lines])
  const sel = useSelection(mains, filtered.filter((l) => l.role === 'main'))
  const picked = () => lines.filter((l) => sel.has(l.key))
  const name = (l: HardLine) => `${l.payer}${l.state ? ` (${l.state})` : ''}${l.from ? ` from ${shortDate(l.from)}` : ''}`
  const samePending = (l: HardLine, x: { payer: string; state: string | null; from: string | null }) =>
    low(x.payer) === low(l.payer) && x.state === l.state && x.from === l.from
  const unpick = (l: HardLine) => setPending((p) => p.filter((x) => !samePending(l, x)))

  /**
   * A plain weight (Shayna #163): 1 is a normal payer. Blank, or 1 on the all-states
   * line: no rule. On a state or dated line, 1 is a setting: a normal payer there or
   * from then, whatever the all-states line says; blank removes the setting.
   */
  const setWeight = (l: HardLine, n: number | null) => {
    const r = l.row
    if (n === null || (n === 1 && !l.state && !l.from)) {
      unpick(l)
      if (r?.active) {
        const what = l.state || l.from ? `${name(l)} setting removed.` : `${l.payer} is no longer a hard payer.`
        return run(what, () => api.update('hard_payer', r.id, { active: false }))
      }
      return
    }
    if (r) {
      const patch: Record<string, unknown> = {}
      if (r.weight !== n) patch.weight = n
      if (!r.active) patch.active = true
      if (!Object.keys(patch).length) return
      return run(`${name(l)} saved.`, () => api.update('hard_payer', r.id, patch)).then((ok) => ok && unpick(l))
    }
    return run(`${name(l)} saved as a hard payer.`, () =>
      api.add('hard_payer', {
        payerName: l.payer,
        matchKind: l.pattern ? 'pattern' : 'exact',
        state: l.state,
        ...(l.from ? { effectiveFrom: l.from } : {}),
        weight: n,
      }),
    ).then((ok) => ok && unpick(l))
  }

  const pickState = (l: HardLine, st: string) => {
    setStateFor(null)
    const existing = rules.hard.find(
      (r) =>
        r.matchKind === (l.pattern ? 'pattern' : 'exact') &&
        low(r.payerName) === low(l.payer) &&
        low(r.state) === low(st) &&
        !r.effectiveFrom,
    )
    // Starts as the payer's all-states extra when it has one; otherwise waits for a number.
    if (existing || l.row?.active) {
      const weight = l.row?.active ? l.row.weight : existing!.weight
      void run(`${l.payer} (${st}) added.`, () =>
        existing
          ? api.update('hard_payer', existing.id, { active: true, weight })
          : api.add('hard_payer', { payerName: l.payer, matchKind: l.pattern ? 'pattern' : 'exact', state: st, weight }),
      )
    } else setPending((p) => [...p, { payer: l.payer, state: st, from: null }])
  }

  /**
   * A change from a bill date: a line that waits for its weight, showing what is in force
   * then. Saving a copy of the line's weight straight away made a second line that
   * changed nothing (#584).
   */
  const pickDate = (l: HardLine, day: string) => {
    setDateFor(null)
    setPending((p) => [...p, { payer: l.payer, state: l.state, from: day }])
  }

  /**
   * The ticked lines at one weight, for all dates or from one. Weight 1 on an all-states
   * line for all dates is a normal payer, so it switches the rule off; anywhere else it
   * is a setting (normal in that state, or from that date).
   */
  const bulkWeight = (w: number | null, day: string | null, reason: string | null) => {
    const ls = picked()
    // By id for a saved line, else one row per payer, state and start date: a payer's
    // line and its dated lines moved to one date are the same setting (#399).
    const rows = new Map<string, Record<string, string>>()
    let unset = 0
    for (const l of ls) {
      const why: Record<string, string> = reason ? { reason } : {}
      if (w === null) {
        // Just the reason: on the lines that are set.
        if (l.row?.active) rows.set(`#${l.row.id}`, { id: String(l.row.id), ...why })
        else unset++
        continue
      }
      const from = day ?? l.from
      if (w === 1 && !l.state && !from) {
        if (l.row?.active) rows.set(`#${l.row.id}`, { id: String(l.row.id), active: 'false' })
        continue
      }
      if (!day && l.row) {
        rows.set(`#${l.row.id}`, { id: String(l.row.id), weight: String(w), active: 'true', ...why })
        continue
      }
      const k = `${l.pattern ? 'pattern' : 'exact'}|${low(l.payer)}|${low(l.state)}|${from ?? ''}`
      if (rows.has(k)) continue
      rows.set(k, {
        payerName: l.payer,
        matchKind: l.pattern ? 'pattern' : 'exact',
        ...(l.state ? { state: l.state } : {}),
        ...(from ? { effectiveFrom: from } : {}),
        weight: String(w),
        active: 'true',
        ...why,
      })
    }
    const what =
      w === null
        ? `Reason on ${plural(ls.length - unset, 'payer')}`
        : `Weight ${w}${day ? ` from ${shortDate(day)}` : ''}${reason ? ' and the reason' : ''} on ${plural(ls.length, 'payer')}`
    const notSet = unset ? ` ${plural(unset, 'payer')} with no weight set got no reason: set a weight first.` : ''
    const done = () => {
      setPending((p) => p.filter((x) => !ls.some((l) => samePending(l, x))))
      sel.clear()
    }
    if (!rows.size) {
      done()
      return void run(`Nothing to change.${notSet}`, async () => {})
    }
    let summary = ''
    void run(
      () => summary,
      async () => {
        summary = `${what}: ${importSummary(await bulkSave(api, 'hard_payer', [...rows.values()]))}${notSet}`
      },
    ).then((ok) => ok && done())
  }
  const bulkReset = () => {
    const ls = picked()
    const rows = ls.filter((l) => l.row?.active).map((l) => ({ id: String(l.row!.id), active: 'false' }))
    setPending((p) => p.filter((x) => !ls.some((l) => samePending(l, x))))
    if (!rows.length) return sel.clear()
    void run(`${plural(rows.length, 'payer')} set back to normal.`, () => bulkSave(api, 'hard_payer', rows)).then(
      (ok) => ok && sel.clear(),
    )
  }

  const setCount = rules.hard.filter((r) => r.active).length
  const exportRows: HardPayerExportRow[] = filtered.map((l) => {
    const hard = !!l.row?.active
    const main = l.role === 'main'
    const kindOf = (r: HardPayer) => r.matchKind === (l.pattern ? 'pattern' : 'exact')
    const before = l.from && !hard ? settingBefore(rules.hard, l.payer, l.state, l.from, kindOf) : null
    return {
      payer: l.payer,
      pattern: l.pattern,
      role: l.role,
      state: l.state,
      from: l.from,
      payerTypes: l.pattern ? [] : (typesOf.get(low(l.payer)) ?? []),
      claims:
        l.role === 'history'
          ? 0
          : vocab.days
            ? lineClaims(vocab.days, { kind: l.pattern ? 'pattern' : 'exact', ...l, from: main ? null : l.from })
            : null,
      weight: hard ? l.row!.weight : (before?.weight ?? 1),
      set: hard,
      reason: hard ? l.row!.reason || null : null,
    }
  })

  return (
    <>
      <p className="body-note rules-lede">
        Every payer in the data, most claims first, each at weight 1 until you set it. Set a harder payer’s weight above 1
        (1.5 counts each claim as one and a half). To make it different in one facility state, or from a date, use the
        payer’s menu.
        <Info
          def={{
            label: 'Weight',
            text: 'What one of the payer’s claims counts as: 1 is a normal claim (every payer starts at 1), 1.5 is one and a half. A state’s own setting wins over the all-states one for claims at that state’s facilities, and a dated change from its date on; 1 there means a normal payer there. Only when weighted claims are switched on.',
          }}
        />
      </p>
      <DatesNote />
      <Toolbar
        q={q}
        setQ={setQ}
        only={only}
        setOnly={setOnly}
        onlyLabel={`Only hard payers (${setCount})`}
        shown={filtered.length}
        total={lines.length}
        noun="payer"
      >
        <PayerFilters value={filter} onChange={setFilter} totals={vocab.totals ?? []} />
      </Toolbar>
      <SelectBar sel={sel} shownCount={filtered.length} noun="payer">
        <BulkWeight label="Weight" min={0} reasonLabel="Reason" onApply={bulkWeight} />
        <button type="button" className="btn" onClick={bulkReset}>
          Set back to normal
        </button>
      </SelectBar>
      {!vocab.totals && (
        <p className="dim" style={{ fontSize: 12 }}>
          {vocab.claimsError ? `The payers in the data are unavailable: ${vocab.claimsError}` : 'Loading the payers in the data…'}
        </p>
      )}
      <ExportBar filename="rules-hard-payers.csv" rows={exportRows} columns={HARD_PAYER_EXPORT} />
      <div className="rules-grid rules-hard" role="table" aria-label="Hard payers">
        <div className="rules-row head" role="row">
          <SelectAllBox sel={sel} label="Select every payer shown" />
          <SortHead col="payer" label="Payer" sort={sort} setSort={setSort} />
          <SortHead col="type" label="Payer type" sort={sort} setSort={setSort} />
          <SortHead col="claims" label="Claims" sort={sort} setSort={setSort} num />
          <span role="columnheader">Weight</span>
          <span role="columnheader">Reason</span>
          <span role="columnheader" aria-label="More" />
        </div>
        {filtered.length === 0 && (
          <div className="empty-note" role="row">
            {only ? 'No hard payers yet.' : q ? 'No payer matches that search.' : 'No payers in the data.'}
          </div>
        )}
        {paged.rows.map((l) => {
          const hard = !!l.row?.active
          const set = hard || l.state !== null || l.from !== null
          const kindOf = (r: HardPayer) => r.matchKind === (l.pattern ? 'pattern' : 'exact')
          const before = l.from && !hard ? settingBefore(rules.hard, l.payer, l.state, l.from, kindOf) : null
          const noChange = l.role === 'sub' && hard && changesNothing(rules.hard, l.row!, ['weight'], { weight: 1 }, kindOf)
          const main = l.role === 'main'
          return (
            <div key={l.key} role="row" className={rowClass(l.role, set, inBlock.has(l.block))}>
              <span role="cell" className="rules-pick-cell">
                {main && <input type="checkbox" aria-label={`Select ${l.payer}`} checked={sel.has(l.key)} onChange={() => sel.toggle(l.key)} />}
              </span>
              <span role="cell" className="rules-name">
                {main ? (
                  <span className="rules-payer">
                    {l.pattern ? <>Payer name {describeMatch('pattern', l.payer)}</> : l.payer}
                  </span>
                ) : (
                  <span className="rules-subline">{subLabel(l)}</span>
                )}
                {main && l.from && <span className="rules-sub">In force from {shortDate(l.from)}</span>}
                <HistoryToggle n={l.nHistory} open={folds.has(l.fold)} onToggle={() => folds.toggle(l.fold)} />
                {l.role === 'history' && <HistoryNote start={vocab.dataStart} />}
                {hard && l.row!.weight === 1 && (l.state || l.from) && (
                  <span className="rules-sub">Not a hard payer{l.state ? ` in ${l.state}` : ''}{l.from ? ` from ${shortDate(l.from)}` : ''}</span>
                )}
                {!main && (l.state || l.from) && !l.row?.active && <span className="rules-sub">Type a weight to save this setting</span>}
                {noChange && (
                  <span className="rules-sub rules-warn">
                    Same as before {shortDate(l.from!)}: changes nothing. Change the weight, or ⋯ → Remove this change.
                  </span>
                )}
              </span>
              <span role="cell" className="dim rules-type" data-label="Payer type" title={(typesOf.get(low(l.payer)) ?? []).join(', ')}>
                {main && !l.pattern ? typesText(typesOf.get(low(l.payer))) : ''}
              </span>
              <span role="cell" className="num rules-claims" data-label="Claims">
                {l.role === 'history' ? '0' : vocab.days ? int(lineClaims(vocab.days, { kind: l.pattern ? 'pattern' : 'exact', ...l, from: main ? null : l.from })) : '…'}
              </span>
              <span role="cell" data-label="Weight" className="rules-extra">
                {/* Shown as what a claim counts as (1.5); stored as the extra over 1 (0.5). */}
                <NumberCell
                  value={hard ? l.row!.weight : null}
                  allowBlank
                  min={0}
                  max={100}
                  placeholder={String(before?.weight ?? 1)}
                  label={`${name(l)}: weight`}
                  onCommit={(n) => setWeight(l, n)}
                />
              </span>
              <span role="cell" data-label="Reason">
                <TextCell
                  value={l.row?.reason ?? ''}
                  disabled={!hard}
                  label={`${name(l)}: reason`}
                  placeholder={hard ? 'Why (optional)' : ''}
                  onCommit={(t) => l.row && void run(`${name(l)} saved.`, () => api.update('hard_payer', l.row!.id, { reason: t || null }))}
                />
              </span>
              <span role="cell" className="rules-menu-cell">
                {dateFor === l.key ? (
                  <DayPick label="Change from (bill date)" onPick={(d) => pickDate(l, d)} onCancel={() => setDateFor(null)} />
                ) : stateFor === l.key ? (
                  <select
                    className="rules-input"
                    autoFocus
                    aria-label="State"
                    defaultValue=""
                    onBlur={() => setStateFor(null)}
                    onChange={(e) => e.target.value && pickState(l, e.target.value)}
                  >
                    <option value="">State…</option>
                    {vocab.states
                      .filter((s) => !lines.some((x) => low(x.payer) === low(l.payer) && x.pattern === l.pattern && low(x.state) === low(s)))
                      .map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                  </select>
                ) : (
                  <Menu
                    label="More"
                    items={[
                      {
                        label: 'History',
                        disabled: !l.row,
                        onClick: () => l.row && onHistory({ table: 'hard_payer', id: l.row.id, title: name(l) }),
                      },
                      ...(main ? [{ label: 'Set differently for one state…', onClick: () => setStateFor(l.key) }] : []),
                      ...(main || l.role === 'state' ? [{ label: 'Change from a date…', onClick: () => setDateFor(l.key) }] : []),
                      l.from || l.role === 'history'
                        ? { label: 'Remove this change', onClick: () => void setWeight(l, null) }
                        : l.state
                          ? { label: 'Remove this state’s setting', onClick: () => void setWeight(l, null) }
                          : { label: 'Not a hard payer', disabled: !hard, onClick: () => void setWeight(l, null) },
                    ]}
                  />
                )}
              </span>
            </div>
          )
        })}
      </div>
      {paged.more}
    </>
  )
}

/* ------------------------------------------------------------------ history */

const FIELD_LABEL: Record<string, string> = {
  matchField: 'Match on',
  matchKind: 'Match kind',
  matchValue: 'Payer',
  creditedDepartment: 'Credited to',
  priority: 'Order',
  note: 'Note',
  active: 'On',
  payerName: 'Payer',
  state: 'State',
  weight: 'Weight',
  includeForHmo: 'Counts for HMO',
  includeForMedicare: 'Counts for Medicare',
  extraWeight: 'Weight',
  effectiveFrom: 'From',
  effectiveTo: 'Until',
  reason: 'Reason',
  payerType: 'Payer type',
  setting: 'Setting',
  value: 'Days',
}

/* ------------------------------------------------------------------ payer types (#695) */

/** One payer type in the data: its resolved claims and the no-touch ones over each step. */
interface PTLine {
  payerType: string
  /** No touch plus touched: every resolved claim of the payer type. */
  resolved: number
  noTouch: number
  touched: number
  /** No-touch claims resolved more than d days after the date ages count from, by step. */
  over: number[]
  /** Share of its no-touch claims with a real clearinghouse send date (#911); null: unknown. */
  realSend: number | null
}

/**
 * The payer types in the bundle, with what each limit would do to their 0% touch share:
 * off the month rows' `agedNoTouch` groups, which carry each payer type's no-touch,
 * touched and aged claims as the bundle has them, whatever limits are applied.
 */
function usePayerTypeLines(): { lines: PTLine[]; hasAges: boolean; fromSend: boolean } {
  const { dataset } = useDataset()
  return useMemo(() => {
    if (!dataset || !hasAgedCounts(dataset)) return { lines: [], hasAges: false, fromSend: false }
    const types = dataset.agedPayerTypes ?? []
    const totals = payerTypeTotals(dataset.touchMonths, types)
    const real = dataset.agedRealSendShare
    const lines = [...totals]
      .map(([payerType, t]) => ({
        payerType,
        resolved: t.noTouch + t.touched,
        ...t,
        realSend: real?.[types.indexOf(payerType)] ?? null,
      }))
      .filter((l) => l.resolved > 0)
      .sort((a, b) => b.resolved - a.resolved)
    return { lines, hasAges: true, fromSend: dataset.agedFrom === 'sendDate' }
  }, [dataset])
}

/**
 * Every payer type together at one limit, for the lede's sense of scale: computed from
 * the data loaded, so it follows the date the ages count from (#911). It said fixed
 * Jan–Sep 2026 figures before, which the move to the send date made wrong.
 */
export function shareAllAt(lines: readonly PTLine[], step: AgeStep | null): number | null {
  let noTouch = 0
  let base = 0
  for (const l of lines) {
    const aged = overAt(l, step)
    noTouch += l.noTouch - aged
    base += l.resolved - aged
  }
  return base <= 0 ? null : noTouch / base
}

/** The no-touch claims a limit takes out (0 with no limit). */
const overAt = (l: PTLine, step: AgeStep | null) => (step === null ? 0 : (l.over[AGE_STEPS.indexOf(step)] ?? 0))

/** A payer type's 0% touch share at a limit (null: no limit): the aged claims leave numerator and base. */
const shareAt = (l: PTLine, step: AgeStep | null) => {
  const aged = overAt(l, step)
  const base = l.resolved - aged
  return base <= 0 ? null : (l.noTouch - aged) / base
}

/**
 * Review #695 as Shayna rewrote it (2026-10-18): "The aging threshold (X days) must be
 * configurable by payer type within the Rules settings." One limit per payer type, from
 * the days the bundle carries; no limit is the figure as it was. Saved as a
 * payer_type_setting row; "No limit" switches the row off (nothing is deleted).
 */
function PayerTypesTab({ rules, api, run, onHistory }: TabProps) {
  const { lines, hasAges, fromSend } = usePayerTypeLines()
  const saved = rules.payerTypes ?? []
  const rowOf = (t: string) => saved.find((r) => low(r.payerType) === low(t) && r.setting === 'zero_touch_max_age_days') ?? null
  // Saved payer types the data no longer has stay listed, so their limit can be changed.
  const shown: (PTLine | { payerType: string; missing: true })[] = [
    ...lines,
    ...saved
      .filter((r) => r.active && !lines.some((l) => low(l.payerType) === low(r.payerType)))
      .map((r) => ({ payerType: r.payerType, missing: true as const })),
  ]

  const setLimit = (payerType: string, row: PayerTypeSetting | null, value: AgeStep | null) => {
    const what = value === null ? `${payerType}: no limit.` : `${payerType}: ${value} days.`
    if (value === null) return row?.active ? run(what, () => api.update('payer_type_setting', row.id, { active: false })) : undefined
    if (!row) return run(what, () => api.add('payer_type_setting', { payerType, setting: 'zero_touch_max_age_days', value }))
    return run(what, () => api.update('payer_type_setting', row.id, { value, active: true }))
  }
  const exportRows: PayerTypeExportRow[] = shown.map((l) => {
    const row = rowOf(l.payerType)
    const limit = row?.active ? (row.value as AgeStep) : null
    const data = 'missing' in l ? null : l
    return {
      payerType: l.payerType,
      resolved: data ? data.resolved : null,
      share: data ? shareAt(data, limit) : null,
      over: data && limit !== null ? overAt(data, limit) : null,
      limit,
      reason: row?.active ? row.reason || null : null,
      realSend: data ? data.realSend : null,
    }
  })

  return (
    <>
      <p className="body-note rules-lede" data-testid="aged-from">
        A resolved claim with no FCC touch that took longer than its payer type’s limit to resolve is taken as worked
        outside the FCC aging workflow tool.{' '}
        {fromSend ? (
          <>
            Its age counts from the day the claim was <b>sent to the payer</b>: the clearinghouse release date, or the
            last day of the service month where the clearinghouse has no match.
          </>
        ) : (
          <>Its age counts from the 1st of the service month, the only bill date this data has.</>
        )}{' '}
        It is left out of the 0% touch share altogether, and shows on Touch profile as <i>Over the age limit</i>, with
        its count and share. No limit: every no-touch claim counts, as before. Changes reach every figure within a
        minute.
      </p>
      {lines.length > 0 && (
        <p className="dim rules-dates-note" data-testid="aged-scale">
          For scale (all payer types, the data loaded): {pct(shareAllAt(lines, null))} of resolved claims have no FCC
          touch; with every payer type at{' '}
          {[...AGE_STEPS]
            .reverse()
            .map((s) => `${s} days ${pct(shareAllAt(lines, s))}`)
            .join(', ')}
          . Each payer type’s own figures are in its menu below.
        </p>
      )}
      {!hasAges && (
        <div className="rules-banner" role="note">
          This data was built before age limits by payer type, so a limit saved now applies from the next refresh.
        </div>
      )}
      <ExportBar filename="rules-payer-types.csv" rows={exportRows} columns={PAYER_TYPE_EXPORT} />
      <div className="rules-grid rules-depts" role="table" aria-label="Payer type 0% touch age limits">
        <div className="rules-row head" role="row">
          <span role="columnheader">Payer type</span>
          <span role="columnheader" className="num">Resolved claims</span>
          <span role="columnheader" className="num">0% touch share</span>
          <span role="columnheader" className="num">Over the limit</span>
          <span role="columnheader">Limit</span>
          <span role="columnheader">Reason</span>
          <span role="columnheader" aria-label="More" />
        </div>
        {shown.length === 0 && (
          <div className="empty-note" role="row">
            No payer types in the data yet.
          </div>
        )}
        {shown.map((l) => {
          const row = rowOf(l.payerType)
          const limit = row?.active ? (row.value as AgeStep) : null
          const data = 'missing' in l ? null : l
          return (
            <div key={l.payerType} role="row" className={`rules-row${limit !== null ? ' is-set' : ''}`}>
              <span role="cell" className="rules-name">
                <span className="rules-payer">{l.payerType}</span>
                {!data && <span className="rules-sub">Not in the data loaded</span>}
                {data && fromSend && data.realSend !== null && (
                  <span
                    className="rules-sub"
                    data-testid="real-send"
                    title="Of this payer type’s no-touch claims, the share with a real clearinghouse release date. The rest count from the last day of the service month."
                  >
                    {pct(data.realSend)} with a real send date
                  </span>
                )}
              </span>
              <span role="cell" className="num" data-label="Resolved claims">
                {data ? int(data.resolved) : '—'}
              </span>
              <span role="cell" className="num" data-label="0% touch share" title={limit === null ? 'No limit' : `At ${limit} days`}>
                {data ? pct(shareAt(data, limit)) : '—'}
              </span>
              <span role="cell" className="num" data-label="Over the limit">
                {data && limit !== null ? `${int(overAt(data, limit))} · ${pct(overAt(data, limit) / data.resolved)}` : '—'}
              </span>
              <span role="cell" data-label="Limit">
                <select
                  className="rules-input"
                  aria-label={`${l.payerType}: 0% touch age limit`}
                  value={limit === null ? '' : String(limit)}
                  onChange={(e) => void setLimit(l.payerType, row, e.target.value === '' ? null : (Number(e.target.value) as AgeStep))}
                >
                  <option value="">No limit{data ? ` · ${pct(shareAt(data, null))}` : ''}</option>
                  {AGE_STEPS.map((s) => (
                    <option key={s} value={String(s)}>
                      Over {s} days{data ? ` · ${pct(shareAt(data, s))}` : ''}
                    </option>
                  ))}
                </select>
              </span>
              <span role="cell" data-label="Reason">
                <TextCell
                  value={row?.reason ?? ''}
                  disabled={!row?.active}
                  label={`${l.payerType}: reason`}
                  placeholder={row?.active ? 'Why (optional)' : ''}
                  onCommit={(t) => row && void run(`${l.payerType} saved.`, () => api.update('payer_type_setting', row.id, { reason: t || null }))}
                />
              </span>
              <span role="cell" className="rules-menu-cell">
                <Menu
                  label="More"
                  items={[
                    {
                      label: 'History',
                      disabled: !row,
                      onClick: () => row && onHistory({ table: 'payer_type_setting', id: row.id, title: `${l.payerType}: 0% touch age limit` }),
                    },
                  ]}
                />
              </span>
            </div>
          )
        })}
      </div>
      <p className="dim rules-dates-note">
        The payer type is the claim’s, as the payer type filter shows it (Unknown payers under the Budget billing group
        read Income/Budget). The share is over the data loaded; each option in the menu shows the share at that limit,
        with the over-the-limit claims left out.
      </p>
    </>
  )
}

function describeRow(rules: RuleSet | null, table: RuleTable, id: number): string {
  if (!rules) return `#${id}`
  if (table === 'coinsurance_biller_rule') {
    const r = rules.biller.find((x) => x.id === id)
    return r ? billerTitle(r) : `#${id}`
  }
  if (table === 'payer_type_setting') {
    const d = (rules.payerTypes ?? []).find((x) => x.id === id)
    return d ? `${d.payerType}: 0% touch age limit` : `#${id}`
  }
  const r = (table === 'coinsurance_payer' ? rules.payers : rules.hard).find((x) => x.id === id)
  return r ? r.payerName + ('state' in r && r.state ? ` (${r.state})` : '') : `#${id}`
}

const shown = (field: string | null, v: string | null) =>
  v === null
    ? '—'
    : field === 'active' || field?.startsWith('includeFor')
      ? v === 'true' || v === '1'
        ? 'yes'
        : 'no'
      : // A hard payer's weight is stored as the extra over 1; it reads as the weight.
        field === 'extraWeight' && Number.isFinite(Number(v))
        ? String(Math.round((1 + Number(v)) * 10_000) / 10_000)
        : v

function HistoryList({ load, rules, focus }: { load: Load<AuditEntry[]>; rules: RuleSet | null; focus: HistoryFocus }) {
  const entries =
    load.state === 'ok'
      ? load.data.filter((e) => (focus.table === null || e.table === focus.table) && (focus.id === null || e.rowId === focus.id))
      : []
  return (
    <>
      <h2 className="section-heading" style={{ marginTop: 0 }}>
        History
      </h2>
      <p className="body-note" style={{ marginTop: 4 }}>
        {focus.id === null ? 'Every change to the rules, newest first (the latest 500).' : focus.title}
      </p>
      {load.state === 'loading' && <p className="eyebrow">Loading history…</p>}
      {load.state === 'error' && <Notice what={load.message} />}
      {load.state === 'ok' && (
        <ul className="rules-history">
          {entries.length === 0 && <li className="empty-note">No changes yet.</li>}
          {entries.map((e) => (
            <li key={e.id}>
              <div className="dim" style={{ fontSize: 12 }}>
                {new Date(e.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ·{' '}
                {e.actor}
                {e.source === 'import' && <span className="pill" style={{ marginLeft: 6 }}>CSV</span>}
              </div>
              {focus.id === null && (
                <div style={{ fontSize: 13 }}>
                  <span className="dim">{TABLE_LABEL[e.table]}:</span> {describeRow(rules, e.table, e.rowId)}
                </div>
              )}
              <div style={{ fontSize: 13 }}>
                {e.action === 'add' ? (
                  'Added'
                ) : e.field === 'priority' ? (
                  // The page orders rules by position; the numbers behind it mean nothing to a reader.
                  <span title={`Priority ${e.oldValue ?? '—'} → ${e.newValue ?? '—'}`}>Moved in the order</span>
                ) : (
                  <>
                    {FIELD_LABEL[e.field ?? ''] ?? e.field}: <span className="dim">{shown(e.field, e.oldValue)}</span> →{' '}
                    {shown(e.field, e.newValue)}
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
